import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  IconButton,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TuneIcon from '@mui/icons-material/Tune';

import { api } from '../../services/api';
import AnalyzerHeader from './AnalyzerHeader';
import ScoreCard from './ScoreCard';
import { algorithmCatalog, ALGO_OPTIMIZE } from './algorithmCatalog';
import { formatPct, isFiniteNumber } from './scoreUtils';
import NeighborDataChipConnected from './NeighborDataChipConnected';
import NumericParamField from './NumericParamField';
import { parseIntField } from '../../utils/numericFieldUtils';
import { NeighborDataIncompleteCard } from './NeighborDataStatus';
import { useAnalyserGate } from './useAnalyserGate';
import { useNeighborData } from './NeighborDataContext';
import {
  DEFAULT_MIN_FREQ_GAP_MHZ,
  DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM,
} from '../../utils/channelMapUtils';
import { parseTreemgmtChannelList } from '../../utils/treemgmtChannelUtils';
import {
  formatUniiOption,
  summarizeUniiSelection,
  UNII_CHANNEL_GROUPS,
  defaultOptimizeChannelGroupSummary,
} from '../../utils/uniiChannelUtils';
import {
  buildOptimizeScopePayload,
  listCapDevices,
  optimizeScopeHelpText,
  resolveOptimizeScopeSerials,
  scopeSummaryLabel,
} from '../../utils/optimizeScopeUtils';
import { useOptimizeScope } from './OptimizeScopeContext';
import MetaPipelineView from './MetaPipelineView';
import InputDebugPanel from './InputDebugPanel';

// Composite gauge tone — kept restrained: primary for healthy /
// medium-healthy, neutral text colour otherwise. We deliberately
// avoid the warning/error palette so the panel doesn't flash yellow
// or red just because the score is sub-optimal — the SummaryText
// already explains the verdict.
const compositeRingColor = (value) => {
  if (!isFiniteNumber(value)) return 'inherit';
  if (value >= 60) return 'primary';
  return 'inherit';
};

// Global same-chassis floor sent to optimize API. -1 opts out of the 320 MHz
// backend default; per-role cap/hop values override via apSameChassisFloor().
const OPTIMIZE_GLOBAL_SAME_CHASSIS_MIN_SEP_MHZ = -1;

// Mirrors the backend isRrmEligibleIperfRun: run must include hop + e2e
// coverage. New runs store explicit flags; legacy runs only set test_mode.
// test_mode="custom" is OK as long as include_hop and include_e2e are set.
const isRrmEligibleIperfRun = (run, { scopedOptimize = false } = {}) => {
  if (!run || run.status !== 'completed') return false;
  if (!scopedOptimize && run.is_site_wide !== true) return false;

  // Explicit flags present (new-style runs, including those with all_to_cap)
  const hasExplicitFlags = run.include_hop !== undefined || run.include_e2e !== undefined || run.include_all_to_cap !== undefined;
  if (hasExplicitFlags) {
    return !!run.include_hop && !!run.include_e2e;
  }

  // Legacy: only test_mode set
  const mode = String(run.test_mode || '').trim().toLowerCase();
  return mode === '' || mode === 'both';
};

/** Isolated cohort scopes use server auto-pick (per-CAP merge when needed). */
const shouldAutoPickIperfPerSegment = ({ optimizeScopeIsSiteWide }) => !optimizeScopeIsSiteWide;

// Friendly labels for the per-step accordion. Colours intentionally
// omitted — every chip in the timeline uses the same neutral outline
// so the eye reads the table rather than the rainbow.
const STEP_LABEL = {
  health:   'Health',
  topology: 'Topology',
  power:    'TX Power',
  channel:  'Channels',
  backup:   'Backup',
};

// One-paragraph explainers shown right under each step's title in the
// meta-analysis result. The intent is to frame *what the step is
// actually answering* before the operator looks at the score / actions
// table — so a 0-action step never reads as "nothing happened" when
// in fact the step did its job and concluded the network was already
// good on this dimension. Each blurb is written from the operator's
// point of view, not the algorithm's.
const STEP_DESCRIPTION = {
  health: (
    <>
      <strong>What this step shows:</strong> a single-snapshot triage of the mesh — which
      devices are offline, have weak uplinks, long paths, high latency/jitter, or elevated
      TX-failure rates. When devices report <em>rf_telemetry</em>, four additional issue
      kinds are surfaced: <strong>airtime_saturation</strong> (channel busy ≥ 60%),{' '}
      <strong>external_interference</strong> (foreign airtime ≥ 25%, where foreign =
      channel_busy − own TX/RX),{' '}
      <strong>rf_errors</strong> (FCS error rate or ACK failure rate ≥ 5%), and{' '}
      <strong>link_retries_burst</strong> (per-station recent retry rate ≥ 20%, computed
      server-side from cached deltas, capped at 10 per device). Each new issue type
      suggests which deeper analyser to run next. Purely advisory: it never re-parents,
      re-channels, or changes power. Devices without rf_telemetry get the classic checks only.
    </>
  ),
  topology: (
    <>
      <strong>What this step shows:</strong> proposed re-parenting moves that lower the
      total weighted tree cost and raise weak-path throughput p10. When the child reports
      an uplink profile, this step uses the child&apos;s STA-mode radio histograms as the
      primary edge signal: dominant MCS + NSS + BW → estimated PHY kbps. Parent-side
      delivered kbps, Ack_Signal_Avg and scan RSSI are fallbacks. When{' '}
      <em>rf_telemetry</em> is available: <strong>phy_rate_mbps</strong> is preferred over
      tx_rate.bitrate for the kbps base; <strong>recent per-window loss/retry deltas</strong>{' '}
      (computed server-side from cached snapshots) replace cumulative ratios for fresher
      burst detection; idle stations (inactive_ms &gt; 600 s) are marked &quot;telemetry
      stale&quot; so their loss/retry penalty is suppressed. Nodes flagged with{' '}
      <em>link_retries_burst</em> get a <strong>30% discount</strong> on the improvement
      threshold only when the current link is <strong>Poor</strong> (misassociation path).
      Reparent intent is classified as <strong>misassociation_recovery</strong>,{' '}
      <strong>tree_holistic</strong>, <strong>hub_spine_relief</strong> (path-first
      moves off overloaded relay hubs — also skips parent TX/channel defer),
      or suppressed as <strong>local_opportunistic</strong>.
      Before reparenting, the planner may defer to a parent TX raise or parent channel move.
      Holistic moves require Δp10 ≥ 1.5× the base threshold unless the current link is Poor.
      Capped by blast radius; nodes already moved this run are pinned.
      No actions = the planner couldn&apos;t find a re-parent that meaningfully improves
      tree cost without violating capacity / RSSI floor / blast-radius constraints.
    </>
  ),
  power: (
    <>
      <strong>What this step shows:</strong> a tiered, power-first TX audit over two arms.{' '}
      <strong>Lower</strong> — trim over-powered radios toward the operating tier (−75 dBm at
      the receiver).{' '}
      <strong>Raise</strong> — only when receiver RSSI drops below the coverage floor
      (−79 dBm); targets the floor, capped at 18 dBm and +3 dB per plan step.{' '}
      <strong>Intra-device coupling</strong> keeps STA/AP (and dual CAP slots) within 2 dB.{' '}
      When <em>rf_telemetry</em> is available, two safety gates apply to <strong>lowers
      only</strong>: TX-drops are refused on radios with{' '}
      <strong>ack_failure_rate ≥ 5%</strong> or <strong>tx_airtime_pct ≥ 40%</strong>.
      When a gate vetoes, the issue is still reported with a <em>SuppressedReason</em>.
      A double-drop guard limits cumulative reduction per radio per plan. No actions = every
      audited radio already meets the tier policy, or rf_telemetry gates prevented further
      lowering.
    </>
  ),
  channel: (
    <>
      <strong>What this step shows:</strong> proposed channel re-assignments that reduce
      the weighted channel-conflict severity across the AP graph (built from GPS distance
      then triaged against the actual neighbour-scan reports). <strong>Conflict severity
      is bandwidth-honest:</strong> two APs are scored by the actual fraction of physical
      spectrum they share at their effective operating bandwidth, not by primary-channel
      distance. Two HT80 APs sitting in the same 80 MHz bonded block (e.g. ch 36 and ch
      48 both occupy 5170-5250 MHz) get full conflict severity — moving the primary
      within the same block is no longer treated as an improvement. The picker also{' '}
      <strong>hard-rejects</strong> any candidate channel that lands in the same bonded
      block as a critical-severity neighbour, so plans always either move the AP into a
      genuinely different block or trigger a bandwidth downgrade. When <em>rf_telemetry</em>{' '}
      is available, conflict severity uses <strong>ForeignAirtimeFraction</strong> (=
      channel_busy − own TX/RX) instead of the legacy lumped BusyFraction — this tells
      the planner which conflicts are driven by <em>external interference</em> vs own
      load. For mixed fleets, each side falls back independently to its BusyFraction when
      rf_telemetry is absent. Candidate channels are further <strong>demoted by FCS-error
      penalty</strong> if a same-block neighbour reports fcs_error_rate above 5%, catching
      non-WiFi RF contamination (microwave, radar). Pair severity is also scaled by
      worst-of-two mean child uplink MCS: low-MCS APs get higher weight because each byte
      consumes more airtime. <strong>Bandwidth awareness:</strong> candidate channels are
      filtered by their maximum supported bandwidth (e.g. channel 165 is excluded at HT80).
      When high-severity conflicts persist, the planner tries <strong>stepwise bandwidth
      downgrades</strong> — first to 40 MHz, then to 20 MHz if 40 MHz didn&apos;t resolve
      the conflict. Each step carries a scaled penalty (larger for bigger drops) so same-BW
      moves are always preferred when comparable. When a child STA is pinned to the CAP
      backhaul channel, the planner prefers moving the child&apos;s downlink AP rather than
      the CAP slot. No actions with issues still listed means conflicts are visible but no
      reassignment lowers total weighted severity mesh-wide (or the global accept gate
      rejected a plan that would not improve enough).
    </>
  ),
  backup: (
    <>
      <strong>What this step shows:</strong> if a node&apos;s current parent dies right
      now, what&apos;s the best other path it has back to a CAP — and how good that backup
      path is compared to the one it&apos;s using today. Edge weights use the
      telemetry-fused <em>EffectiveLinkCostV2</em> when rf_telemetry is available, so
      backup rankings reflect <strong>phy_rate_mbps</strong> and recent per-window
      loss/retry deltas rather than just RSSI. Purely advisory: nothing is changed on the
      network and the meta plan emits no backup actions; the per-node table below is the
      output.
    </>
  ),
};

const stepActionsCount = (step) =>
  (step.topology_actions?.length || 0) +
  (step.power_actions?.length || 0) +
  (step.channel_actions?.length || 0);

const stepIssuesCount = (step) =>
  (step.topology_issues?.length || 0) +
  (step.power_issues?.length || 0) +
  (step.channel_issues?.length || 0) +
  (step.health_issues?.length || 0);

const flattenAllActions = (steps) => {
  const out = [];
  steps?.forEach((step) => {
    (step.topology_actions || []).forEach((action) => out.push({
      kind: 'topology', step: step.step, iteration: step.iteration, action,
    }));
    (step.power_actions || []).forEach((action) => out.push({
      kind: 'power', step: step.step, iteration: step.iteration, action,
    }));
    (step.channel_actions || []).forEach((action) => out.push({
      kind: 'channel', step: step.step, iteration: step.iteration, action,
    }));
  });
  return out;
};

// Severity colour mirrors HealthPanel — keep the meta panel visually
// consistent with the per-algo panels so an operator who's used to the
// individual analyser tabs reads the meta accordion the same way.
const severityChipColor = (severity) => {
  if (typeof severity !== 'number') return 'default';
  if (severity >= 0.7) return 'error';
  if (severity >= 0.3) return 'warning';
  return 'default';
};

// Normalise an issue row across the four issue shapes the analyser
// emits. Health/topology/power put `serial`, `kind`, `reason`,
// numeric `severity` directly on the issue object; channel issues
// instead use `affected_devices[]`, `band`, `description`, and a
// string severity label (with the numeric value living on
// `severity_score`). Without a per-field fallback the channel rows
// rendered as "Serial: —, Severity: —, Reason: —, Hint: —" which
// gave the operator a 10-row table of dashes.
const normalizeIssue = (issue, kindLabel) => {
  const affected = Array.isArray(issue.affected_devices) ? issue.affected_devices : [];
  const serialList = affected
    .map((d) => d?.serial)
    .filter(Boolean);
  const serialText = issue.serial || (serialList.length > 0 ? serialList.join(' ↔ ') : '');

  const numericSeverity =
    typeof issue.severity === 'number'
      ? issue.severity
      : (typeof issue.severity_score === 'number' ? issue.severity_score : null);
  const severityLabel =
    typeof issue.severity === 'string' && issue.severity.length > 0 ? issue.severity : null;

  const kind = (() => {
    const issueID = String(issue.issue_id || issue.id || '');
    if (issueID.includes('relay_hub_overload')) return 'relay hub overload';
    if (issueID.includes('hub_relief_capacity_blocked')) return 'hub relief blocked';
    return issue.kind || issue.band || kindLabel.toLowerCase();
  })();
  const reason = issue.reason || issue.description || '';

  let hint = issue.suggested || '';
  let hintTooltip = 'Hint about which deeper analyser would address this issue';
  if (!hint) {
    const bits = [];
    if (typeof issue.distance_meters === 'number') bits.push(`${issue.distance_meters.toFixed(1)} m`);
    if (typeof issue.channel_difference === 'number') bits.push(`Δch ${issue.channel_difference}`);
    if (typeof issue.frequency_difference_mhz === 'number') bits.push(`Δf ${issue.frequency_difference_mhz} MHz`);
    // Phase-3: when both radios report airtime busy, show it as
    // "78%/64% busy" so the operator can tell a real load problem
    // apart from a paper conflict between two idle radios.
    if (typeof issue.airtime_busy_pct_a === 'number' && typeof issue.airtime_busy_pct_b === 'number'
        && (issue.airtime_busy_pct_a > 0 || issue.airtime_busy_pct_b > 0)) {
      bits.push(`${issue.airtime_busy_pct_a.toFixed(0)}%/${issue.airtime_busy_pct_b.toFixed(0)}% busy`);
    }
    // Phase E: surface mean child uplink MCS — low MCS pairs amplify
    // the conflict (more airtime per byte). "MCS 3.4/5.1" tells the
    // operator at a glance whether this conflict is between two
    // healthy APs or one that's already crawling.
    if (typeof issue.mean_child_mcs_a === 'number' && typeof issue.mean_child_mcs_b === 'number'
        && (issue.mean_child_mcs_a > 0 || issue.mean_child_mcs_b > 0)) {
      bits.push(`MCS ${issue.mean_child_mcs_a.toFixed(1)}/${issue.mean_child_mcs_b.toFixed(1)}`);
    }
    // Apr 2026 neighbour-evidence triage: GPS distance is now a
    // coarse pre-filter; the planner additionally consults each
    // device's scan reports. Surface "heard at -68 dBm" or
    // "no scan data" so the operator can see WHY this pair is in the
    // list (and trust the analyser more than a pure-GPS verdict).
    if (typeof issue.neighbor_evidence === 'string' && issue.neighbor_evidence.length > 0) {
      if (issue.neighbor_evidence === 'heard' && typeof issue.neighbor_rssi_dbm === 'number' && issue.neighbor_rssi_dbm < 0) {
        bits.push(`heard ${issue.neighbor_rssi_dbm.toFixed(0)} dBm`);
      } else if (issue.neighbor_evidence === 'no_scan_data') {
        bits.push('no scan data');
      } else if (issue.neighbor_evidence === 'not_observed_either_way') {
        bits.push('not in scans');
      }
    }
    if (issue.involves_cap) bits.push('vs CAP');
    if (typeof issue.suppressed_reason === 'string' && issue.suppressed_reason.length > 0) {
      bits.push(`suppressed: ${issue.suppressed_reason}`);
    }
    if (typeof issue.radio_name === 'string' && issue.radio_name.length > 0) {
      bits.push(issue.radio_name);
    }
    if (typeof issue.station_mac === 'string' && issue.station_mac.length > 0) {
      bits.push(issue.station_mac);
    }
    if (bits.length > 0) {
      hint = bits.join(' · ');
      hintTooltip = 'Diagnostic details: conflict geometry, airtime load, mean child uplink MCS, neighbour-scan evidence, rf_telemetry measurements, and suppressed-reason (if an action was vetoed by a safety gate).';
    }
  }
  return { serialText, numericSeverity, severityLabel, kind, reason, hint, hintTooltip };
};

// Renders the per-step issues array (health_issues / topology_issues /
// power_issues / channel_issues) inside the meta accordion. The
// individual analyser panels already have nice issue tables — this is
// the equivalent surface for the chained pipeline so the operator can
// see *why* a step produced 0 actions instead of just "No actions".
const StepIssuesTable = ({ issues, kindLabel }) => {
  if (!issues || issues.length === 0) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}
      >
        {kindLabel} issues ({issues.length})
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 220 }}>Serial</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 130 }}>Kind</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 110 }}>Severity</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 200 }}>Hint</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {issues.map((issue, k) => {
              const n = normalizeIssue(issue, kindLabel);
              const sevText = n.numericSeverity !== null
                ? (n.severityLabel
                    ? `${n.severityLabel} (${n.numericSeverity.toFixed(2)})`
                    : n.numericSeverity.toFixed(2))
                : (n.severityLabel || '—');
              const sevFilled = n.numericSeverity !== null && n.numericSeverity >= 0.3;
              return (
                <TableRow
                  key={issue.issue_id || issue.id || `${n.serialText || 'x'}-${n.kind}-${k}`}
                  hover
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'normal', wordBreak: 'break-all' }}>
                      {n.serialText || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={n.kind} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={sevText}
                      color={severityChipColor(n.numericSeverity)}
                      variant={sevFilled ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {n.reason || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {n.hint ? (
                      <Tooltip title={n.hintTooltip}>
                        <Chip size="small" variant="outlined" label={n.hint} />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Renders the backup-step `backup_entries` array. This is purely
// advisory data (the meta pipeline never emits backup actions), but
// it's what tells the operator "node X has no backup parent at all"
// vs "node X has 2 alternatives but they're both worse paths".
const formatImprovement = (n) => (typeof n === 'number' ? n.toFixed(2) : '—');

const BackupEntriesTable = ({ entries }) => {
  if (!entries || entries.length === 0) return null;
  const withBackups = entries.filter((e) => e.available && (e.backups?.length || 0) > 0);
  const withoutBackups = entries.filter((e) => !e.available || !(e.backups?.length || 0));
  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }} useFlexGap flexWrap="wrap">
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, alignSelf: 'center' }}
        >
          Per-node failover posture
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Chip
          size="small"
          variant="outlined"
          color={withBackups.length > 0 ? 'primary' : 'default'}
          label={`${withBackups.length} with backup`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`${withoutBackups.length} without backup`}
        />
      </Stack>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 150 }}>Node</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 150 }}>Current parent</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 90 }}>Backups</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Best alternative / reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => {
              const best = (entry.backups || [])[0];
              return (
                <TableRow key={entry.node_serial} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {entry.node_serial}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {entry.current_parent || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={entry.available ? 'primary' : 'default'}
                      label={(entry.backups?.length) || 0}
                    />
                  </TableCell>
                  <TableCell>
                    {best ? (
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        → {best.parent_serial} ({best.band || '—'},{' '}
                        {best.rssi_down_dbm ?? '—'} dBm, Δcost{' '}
                        {formatImprovement(best.improvement)}
                        {best.is_cap ? ', via CAP' : ''})
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {entry.reason || 'No alternative path'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Render a kbps value as a short Mbps/kbps string for inline use in
// the per-step action timeline.
const formatKbpsShort = (kbps) => {
  if (!isFiniteNumber(kbps) || kbps <= 0) return null;
  if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`;
  return `${Math.round(kbps)} kbps`;
};

const hasCurrentUplinkProfile = (action) =>
  isFiniteNumber(action?.current_uplink_mcs) ||
  isFiniteNumber(action?.current_uplink_nss) ||
  isFiniteNumber(action?.current_uplink_bw_mhz) ||
  isFiniteNumber(action?.current_uplink_est_kbps);

const hasChannelMeanChildMCS = (issue) =>
  isFiniteNumber(issue?.mean_child_mcs_a) ||
  isFiniteNumber(issue?.mean_child_mcs_b);

const formatIdleAPPlanStats = (stats) => {
  if (!stats) return null;
  const excluded = stats.idle_ap_devices_excluded || [];
  const finalUnsat = stats.unsatisfied_pairs_final;
  switch (stats.idle_ap_retry_outcome) {
    case 'disabled':
      return 'Disabled (idle_ap_retry_disabled) — unused AP devices remain in the heard/conflict graph.';
    case 'no_idle_devices':
      return 'Enabled by default — no unused AP devices detected (every AP is serving mesh children or on the reparent whitelist).';
    case 'primary_excluded':
      return `Enabled by default — excluded ${excluded.length} unused AP device(s) from heard/conflict planning; they are not treated as mesh backhaul conflicts.`;
    case 'primary_excluded_unsatisfied_remain':
      return `Enabled — excluded ${excluded.length} unused AP device(s); ${finalUnsat ?? '?'} unsatisfied heard-pair(s) remain among active APs/CAPs.`;
    case 'retry_applied':
      return `Second pass helped — excluded ${excluded.length} unused AP device(s); unsatisfied pairs ${stats.unsatisfied_pairs_before} → ${stats.unsatisfied_pairs_after}.`;
    case 'retry_skipped_no_improvement':
      return `Second pass ran but did not improve unsatisfied heard-pair count (${finalUnsat ?? '?'} remain).`;
    case 'retry_skipped_no_idle_in_unsatisfied':
      return `Enabled — ${finalUnsat ?? '?'} unsatisfied pair(s) involve only active APs/CAPs (not unused AP endpoints).`;
    default:
      if (stats.idle_ap_retry_enabled) {
        return 'Enabled by default — idle AP graph adjustment active.';
      }
      return null;
  }
};

const formatChannelNoActionReason = (step) => {
  if (step?.step !== 'channel') return null;
  const issueCount = step.channel_issues?.length || 0;
  const actionCount = step.channel_actions?.length || 0;
  if (issueCount === 0 || actionCount > 0) return null;
  const stats = step.channel_plan_stats;
  if (stats?.no_action_summary) {
    return stats.no_action_summary;
  }
  if (stats?.no_net_improving_plan) {
    if (stats.global_accept_gate_dropped && stats.actions_proposed_before_gate > 0) {
      const delta = stats.proposed_severity_delta;
      const deltaText = typeof delta === 'number' && Number.isFinite(delta)
        ? ` (proposed net severity change ${delta >= 0 ? '−' : '+'}${Math.abs(delta).toFixed(2)})`
        : '';
      return `Conflicts remain (${issueCount} issue(s)) but the planner rejected ${stats.actions_proposed_before_gate} proposed change(s) — they would not improve total weighted severity enough${deltaText}. When a child STA is pinned to the CAP backhaul channel, re-channel the child downlink AP instead of the CAP slot.`;
    }
    return `Conflicts remain (${issueCount} issue(s)) but no channel reassignment lowers total weighted severity mesh-wide. Typical fix: move the child downlink radio away from the CAP backhaul slot while the STA uplink stays pinned.`;
  }
  if (step.score?.summary_text) {
    return step.score.summary_text;
  }
  return null;
};

const CHANNEL_NO_ACTION_REASON_META = {
  gate_rejected: {
    title: 'Channel plan rejected — simulated fleet score would not improve enough',
    severity: 'warning',
  },
  no_proposals: {
    title: 'Conflicts visible — planner produced no channel moves',
    severity: 'warning',
  },
  unsatisfied: {
    title: 'Heard-pair constraints could not be satisfied',
    severity: 'error',
  },
  step_filtered: {
    title: 'Channel plan filtered before apply',
    severity: 'info',
  },
  no_net_improvement: {
    title: 'No net-improving channel reassignment',
    severity: 'warning',
  },
};

/** Structured explain block for channel step with issues but zero actions. */
const buildChannelNoActionExplain = (step) => {
  if (step?.step !== 'channel') return null;
  const issueCount = step.channel_issues?.length || 0;
  const actionCount = step.channel_actions?.length || 0;
  if (issueCount === 0 || actionCount > 0) return null;

  const stats = step.channel_plan_stats || {};
  const reason = stats.no_action_reason
    || (stats.global_accept_gate_dropped && stats.actions_proposed_before_gate > 0 ? 'gate_rejected' : null)
    || (stats.unsatisfied_pairs_final > 0 ? 'unsatisfied' : null)
    || (stats.actions_after_gate > 0 ? 'step_filtered' : null)
    || (stats.no_net_improving_plan ? 'no_proposals' : null)
    || 'no_net_improvement';
  const meta = CHANNEL_NO_ACTION_REASON_META[reason] || CHANNEL_NO_ACTION_REASON_META.no_net_improvement;

  const bullets = [];
  if (reason === 'gate_rejected') {
    bullets.push(
      `The planner simulated ${stats.actions_proposed_before_gate} channel change(s) on a copy of the mesh and recomputed total weighted conflict severity (overlap + isolation penalties across all scan-heard pairs).`,
    );
    if (typeof stats.proposed_severity_delta === 'number' && Number.isFinite(stats.proposed_severity_delta)) {
      if (stats.proposed_severity_delta > 0) {
        bullets.push(
          `Net severity would drop by ${stats.proposed_severity_delta.toFixed(2)} units — below the global accept floor (typically 5% of baseline).`,
        );
      } else if (stats.proposed_severity_delta < 0) {
        bullets.push(
          `Net severity would increase by ${Math.abs(stats.proposed_severity_delta).toFixed(2)} units (worse). Fixing one heard pair can raise overlap penalties on other neighbours.`,
        );
      } else {
        bullets.push('Net severity would not change — the plan is not worth applying.');
      }
    }
    if (typeof stats.global_accept_relative_improvement === 'number' && Number.isFinite(stats.global_accept_relative_improvement)) {
      bullets.push(
        `Relative improvement: ${(stats.global_accept_relative_improvement * 100).toFixed(1)}% (need ≥ ~5% by default).`,
      );
    }
  } else if (reason === 'no_proposals') {
    bullets.push(
      'DSATUR did not assign any channel changes that differ from today\'s channels on the planning graph.',
    );
    bullets.push(
      'Free 6 GHz channels may exist, but STA uplink pins, same-chassis rules, idle-AP graph trimming, or ripple effects on other heard pairs block a fleet-wide win.',
    );
  } else if (reason === 'unsatisfied') {
    bullets.push(
      `${stats.unsatisfied_pairs_final ?? '?'} heard-pair constraint(s) remain unsatisfied after planning — not solvable by channel moves alone under current pins.`,
    );
  } else if (reason === 'step_filtered') {
    bullets.push(
      `${stats.actions_after_gate} change(s) passed the global accept gate but were removed by optimize filters (pin, focus scope, or per-action improvement floor).`,
    );
    const filterParts = [];
    if (step.skipped_due_to_pin > 0) filterParts.push(`${step.skipped_due_to_pin} pinned`);
    if (step.skipped_due_to_focus > 0) filterParts.push(`${step.skipped_due_to_focus} outside focus`);
    if (step.skipped_due_to_floor > 0) filterParts.push(`${step.skipped_due_to_floor} below improvement floor`);
    if (filterParts.length > 0) {
      bullets.push(`Filtered: ${filterParts.join(', ')}.`);
    }
  }

  bullets.push(
    'This does not mean isolation is impossible on the listed pair — it means no emitted plan improves the simulated fleet-wide score. Typical manual fix: move the child downlink AP (wifi2), not the CAP backhaul slot (wifi1), when STA uplinks are pinned.',
  );

  const excluded = stats.idle_ap_devices_excluded?.length || 0;
  if (excluded > 0) {
    bullets.push(
      `Planning excluded ${excluded} unused AP device(s) from the heard/conflict graph (idle AP handling).`,
    );
  }

  return {
    reason,
    title: meta.title,
    severity: meta.severity,
    summary: stats.no_action_summary || formatChannelNoActionReason(step),
    bullets,
  };
};

const ChannelNoActionExplainAlert = ({ step }) => {
  const explain = buildChannelNoActionExplain(step);
  if (!explain) return null;
  return (
    <Alert severity={explain.severity} variant="outlined" sx={{ mb: 1.5 }}>
      <AlertTitle sx={{ fontWeight: 600 }}>{explain.title}</AlertTitle>
      {explain.summary && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {explain.summary}
        </Typography>
      )}
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {explain.bullets.map((line) => (
          <Typography key={line} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {line}
          </Typography>
        ))}
      </Box>
      {explain.reason && (
        <Chip
          size="small"
          variant="outlined"
          label={`reason: ${explain.reason}`}
          sx={{ mt: 1 }}
        />
      )}
    </Alert>
  );
};

const summarizeUplinkUsage = (result) => {
  const topologyActions = [];
  const channelIssues = [];
  const channelActions = [];
  let channelPlanStats = null;
  const healthIssues = [];
  const powerIssues = [];
  (result?.steps || []).forEach((step) => {
    if (step.step === 'topology') {
      topologyActions.push(...(step.topology_actions || []));
    }
    if (step.step === 'channel') {
      channelIssues.push(...(step.channel_issues || []));
      channelActions.push(...(step.channel_actions || []));
      if (step.channel_plan_stats) {
        channelPlanStats = step.channel_plan_stats;
      }
    }
    if (step.step === 'health') {
      healthIssues.push(...(step.health_issues || []));
    }
    if (step.step === 'power') {
      powerIssues.push(...(step.power_issues || []));
    }
  });

  const topologyMeasured = topologyActions.filter(hasCurrentUplinkProfile);
  const topologyEstimated = topologyActions.filter((a) => isFiniteNumber(a.proposed_est_kbps) && a.proposed_est_kbps > 0);
  const channelWeighted = channelIssues.filter(hasChannelMeanChildMCS);

  const channelHeard = channelIssues.filter((i) => i?.neighbor_evidence === 'heard').length;
  const channelNoScan = channelIssues.filter((i) => i?.neighbor_evidence === 'no_scan_data').length;
  const channelTriageActive = channelIssues.some(
    (i) => typeof i?.neighbor_evidence === 'string' && i.neighbor_evidence.length > 0,
  );

  const channelBWDowngrades = channelActions.filter(
    (a) => typeof a?.proposed_htmode === 'string' && a.proposed_htmode.length > 0,
  );

  const channelUnsatisfied = channelIssues.filter((i) => String(i?.id || '').includes('-unsatisfied')).length;

  const telemetryHealthKinds = ['airtime_saturation', 'external_interference', 'rf_errors', 'link_retries_burst'];
  const healthTelemetryIssues = healthIssues.filter((i) => telemetryHealthKinds.includes(i?.kind));
  const powerSuppressed = powerIssues.filter((i) => typeof i?.suppressed_reason === 'string' && i.suppressed_reason.length > 0);

  return {
    topologyActions: topologyActions.length,
    topologyMeasured: topologyMeasured.length,
    topologyEstimated: topologyEstimated.length,
    channelIssues: channelIssues.length,
    channelWeighted: channelWeighted.length,
    channelTriageActive,
    channelHeard,
    channelNoScan,
    channelBWDowngrades: channelBWDowngrades.length,
    channelPlanStats,
    channelUnsatisfied,
    idleAPPlanStatsText: formatIdleAPPlanStats(channelPlanStats),
    channelNoActionExplain: (() => {
      const channelStep = (result?.steps || []).find((s) => s.step === 'channel');
      return channelStep ? buildChannelNoActionExplain(channelStep) : null;
    })(),
    example: topologyMeasured[0],
    healthTotalIssues: healthIssues.length,
    healthTelemetryIssues: healthTelemetryIssues.length,
    powerTotalIssues: powerIssues.length,
    powerSuppressed: powerSuppressed.length,
  };
};

const formatActionLine = (entry) => {
  const a = entry.action;
  if (entry.kind === 'topology') {
    // When the parent reports measured throughput for this child,
    // include it in the one-liner so the operator sees *why* the
    // planner moved a "fine RSSI" link.
    const meas = formatKbpsShort(a.current_delivered_kbps);
    const currentUplink = formatKbpsShort(a.current_uplink_est_kbps);
    const proposed = formatKbpsShort(a.proposed_est_kbps);
    const parts = [];
    if (currentUplink) {
      const profile = [
        isFiniteNumber(a.current_uplink_mcs) ? `MCS ${a.current_uplink_mcs}` : null,
        isFiniteNumber(a.current_uplink_nss) ? `NSS ${a.current_uplink_nss}` : null,
        isFiniteNumber(a.current_uplink_bw_mhz) ? `${a.current_uplink_bw_mhz} MHz` : null,
      ].filter(Boolean).join(', ');
      parts.push(`current uplink est ${currentUplink}${profile ? ` (${profile})` : ''}`);
    } else if (meas) {
      parts.push(`measured ${meas}`);
    }
    if (proposed) parts.push(`proposed est ${proposed}`);
    const intent = a.reparent_intent ? String(a.reparent_intent).replace(/_/g, ' ') : null;
    const p10 = isFiniteNumber(a.delta_p10_kbps) ? `Δp10 +${a.delta_p10_kbps} kbps` : null;
    const pathBefore = formatKbpsShort(a.current_path_kbps);
    const pathAfter = formatKbpsShort(a.proposed_path_kbps);
    const pathMeta = pathBefore && pathAfter
      ? `path ${pathBefore}→${pathAfter}${isFiniteNumber(a.current_hops_to_cap) && isFiniteNumber(a.proposed_hops_to_cap)
        ? ` · hops ${a.current_hops_to_cap}→${a.proposed_hops_to_cap}`
        : ''}`
      : null;
    const hubMeta = a.hub_parent_serial ? `off hub ${a.hub_parent_serial}` : null;
    const meta = [intent, p10, pathMeta, hubMeta].filter(Boolean).join(' · ');
    const tail = parts.length > 0 || meta
      ? ` — ${[meta, ...parts].filter(Boolean).join(' · ')}`
      : '';
    return `Re-parent ${a.serial} → ${a.proposed_parent} (${a.band})${tail}`;
  }
  if (entry.kind === 'power') {
    const dirLabel = a.direction === 'uplink' ? '↑ uplink STA' : '↓ downlink AP';
    const delta = a.current_tx_power != null && a.recommended_tx_power != null
      ? a.recommended_tx_power - a.current_tx_power
      : null;
    const deltaStr = delta != null
      ? (delta >= 0 ? ` (+${delta} dB)` : ` (${delta} dB)`)
      : '';
    return `${dirLabel} ${a.serial} ${a.radio_name}: ${a.current_tx_power} → ${a.recommended_tx_power} dBm${deltaStr}`;
  }
  if (entry.kind === 'channel') {
    const serial = a.serial || a.payload?.serial_number;
    const band = a.radio_name || a.band || a.payload?.band;
    const proposed = a.proposed_channel ?? a.payload?.channel ?? a.recommended_channel;
    const current = a.current_channel ?? a.payload?.current_channel;
    // Surface the full channel_groups list (primary + alternates) so
    // operators can see at a glance what fallbacks the device's ACS
    // layer will be allowed to pick from after the plan applies.
    const groupList = Array.isArray(a.proposed_channel_group) && a.proposed_channel_group.length > 1
      ? a.proposed_channel_group
      : null;

    if (!serial && a.label) {
      return a.label;
    }

    const who = [serial, band].filter(Boolean).join(' ') || 'AP';
    const change = current != null && proposed != null
      ? `channel ${current} → ${proposed}`
      : `channel → ${proposed ?? '?'}`;
    const bwChange = a.proposed_htmode
      ? ` [BW → ${a.proposed_htmode}]`
      : '';
    const groupHint = groupList ? ` (ACS may fall back to: ${groupList.slice(1).join(', ')})` : '';
    return `${who}: ${change}${bwChange}${groupHint}`;
  }
  return `${entry.kind} action`;
};

const OptimizeMetaPanel = ({
  onOpenPlan,
  devices = [],
  initialOptimizeScope = null,
  onOptimizeScopeApplied,
} = {}) => {
  const { enqueueSnackbar } = useSnackbar();
  const catalog = algorithmCatalog[ALGO_OPTIMIZE];
  const { refresh: refreshDataStatus } = useNeighborData();
  const { setOptimizeScope } = useOptimizeScope();
  const { missingSerials, clearMissing, runGuarded, scanMissing, scanningSerials } = useAnalyserGate();

  const caps = useMemo(() => listCapDevices(devices), [devices]);
  const [scopeMode, setScopeMode] = useState('site');
  const [selectedCapSerials, setSelectedCapSerials] = useState([]);
  const [mapFocusSerials, setMapFocusSerials] = useState([]);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [applyAllBusy, setApplyAllBusy] = useState(false);
  const [applyProgress, setApplyProgress] = useState({ done: 0, total: 0 });
  // Match backend default: pin non-plan devices to current parent before executing plan actions.
  const [planPersistTree, setPlanPersistTree] = useState(false);
  // tolerantReparent controls whether set-parent actions get the
  // background_sta_scan-assisted flow (default on). The runner
  // already defaults to true when the payload omits the field, but
  // we surface the toggle here so operators can opt out without
  // editing JSON. When false, we stamp `tolerant: false` on every
  // set-parent action's payload to override the backend default.
  const [tolerantReparent, setTolerantReparent] = useState(true);
  const DEFAULT_REPARENT_WHITELIST_COUNT = 3;
  const [reparentWhitelistCount, setReparentWhitelistCount] = useState(DEFAULT_REPARENT_WHITELIST_COUNT);

  // Optional params — match the optimizeRequest shape.
  const DEFAULT_IMPROVEMENT_FLOOR = 1.0;
  const [maxIterations, setMaxIterations] = useState(3);
  const [useHealthFocus, setUseHealthFocus] = useState(false);
  const [rebuildTree, setRebuildTree] = useState(false);
  const [customTrees, setCustomTrees] = useState([]);
  const [selectedCustomTrees, setSelectedCustomTrees] = useState([]);
  // Global channel_bandwidth is derived from the per-role bandwidths
  // (max of the two) so the conflict graph uses the widest planning
  // window. No separate UI control needed.


  useEffect(() => {
    if (!initialOptimizeScope || initialOptimizeScope.type !== 'devices') return;
    const serials = (initialOptimizeScope.serials || []).filter(Boolean);
    if (serials.length === 0) return;
    setScopeMode('devices');
    setMapFocusSerials(serials);
    setSelectedCapSerials([]);
    setSelectedCustomTrees([]);
    onOptimizeScopeApplied?.();
  }, [initialOptimizeScope, onOptimizeScopeApplied]);

  const handleScopeModeChange = (event) => {
    const next = event.target.value;
    setScopeMode(next);
    if (next === 'site') {
      setSelectedCustomTrees([]);
      setMapFocusSerials([]);
      setSelectedCapSerials([]);
      return;
    }
    if (next === 'caps') {
      setSelectedCustomTrees([]);
      setMapFocusSerials([]);
      setSelectedCapSerials([]);
      return;
    }
    if (next === 'trees') {
      setSelectedCapSerials([]);
      setMapFocusSerials([]);
      return;
    }
    if (next === 'devices') {
      setSelectedCustomTrees([]);
      setSelectedCapSerials([]);
    }
  };

  const handleCapScopeChange = (event) => {
    const value = event.target.value;
    const next = typeof value === 'string' ? value.split(',') : value;
    if (next[next.length - 1] === '__all__') {
      const allSerials = selectedCapSerials.length === caps.length
        ? []
        : caps.map((d) => d.serial_number);
      setSelectedCapSerials(allSerials);
      setMapFocusSerials([]);
      setSelectedCustomTrees([]);
      return;
    }
    const capSerials = next.filter((v) => v !== '__all__');
    setSelectedCapSerials(capSerials);
    setMapFocusSerials([]);
    setSelectedCustomTrees([]);
  };

  const handleCustomTreeScopeChange = (event, newValue) => {
    setSelectedCustomTrees(newValue);
    setScopeMode(newValue.length > 0 ? 'trees' : 'site');
    if (newValue.length > 0) {
      setSelectedCapSerials([]);
      setMapFocusSerials([]);
    }
  };

  const clearDeviceScope = () => {
    setMapFocusSerials([]);
    setScopeMode('site');
  };

  const scopeSummary = useMemo(
    () => scopeSummaryLabel(scopeMode, { selectedCapSerials, mapFocusSerials, selectedCustomTrees }, caps),
    [scopeMode, selectedCapSerials, mapFocusSerials, selectedCustomTrees, caps],
  );
  const scopeHelpText = useMemo(
    () => optimizeScopeHelpText(scopeMode, {
      selectedCapSerials,
      mapFocusSerials,
      selectedCustomTrees,
      caps,
    }),
    [scopeMode, selectedCapSerials, mapFocusSerials, selectedCustomTrees, caps],
  );
  const selectedCustomTreeIds = useMemo(
    () => selectedCustomTrees.map((tree) => tree.id).filter(Boolean),
    [selectedCustomTrees],
  );

  const optimizeScopeIsSiteWide = useMemo(() => {
    if (scopeMode === 'devices' && mapFocusSerials.length > 0) return false;
    if (scopeMode === 'trees' && selectedCustomTreeIds.length > 0) return false;
    if (scopeMode !== 'caps' || selectedCapSerials.length === 0) return true;
    return caps.length > 0 && selectedCapSerials.length === caps.length;
  }, [scopeMode, selectedCustomTreeIds.length, mapFocusSerials.length, selectedCapSerials.length, caps.length]);
  const iperfMatchingScope = useMemo(() => ({
    customTreeId: scopeMode === 'trees' && selectedCustomTreeIds.length === 1
      ? selectedCustomTreeIds[0]
      : undefined,
    customTreeIds: scopeMode === 'trees' && selectedCustomTreeIds.length > 1
      ? selectedCustomTreeIds
      : undefined,
    capSerials: scopeMode === 'caps' && selectedCapSerials.length > 0 && !optimizeScopeIsSiteWide
      ? selectedCapSerials
      : undefined,
    focusSerials: scopeMode === 'devices' && mapFocusSerials.length > 0
      ? mapFocusSerials
      : undefined,
  }), [scopeMode, selectedCustomTreeIds, selectedCapSerials, mapFocusSerials, optimizeScopeIsSiteWide]);
  const scopeSerials = useMemo(
    () => resolveOptimizeScopeSerials(scopeMode, {
      selectedCapSerials,
      mapFocusSerials,
      selectedCustomTrees,
      devices,
      optimizeScopeIsSiteWide,
    }),
    [
      scopeMode,
      selectedCapSerials,
      mapFocusSerials,
      selectedCustomTrees,
      devices,
      optimizeScopeIsSiteWide,
    ],
  );

  useEffect(() => {
    setOptimizeScope({
      scopeSerials,
      scopeLabel: scopeSummary,
      isSiteWide: optimizeScopeIsSiteWide,
    });
  }, [scopeSerials, scopeSummary, optimizeScopeIsSiteWide, setOptimizeScope]);

  const [threshold, setThreshold] = useState(0.3);
  const [improvementFloor, setImprovementFloor] = useState(DEFAULT_IMPROVEMENT_FLOOR);
  const [minFrequencySeparationMHz, setMinFrequencySeparationMHz] = useState(DEFAULT_MIN_FREQ_GAP_MHZ);
  // Same-chassis radios (CAP dual-AP / multi-radio Nodes) get a
  // stricter HARD floor than the inter-device "heard" floor above.
  // Default 320 MHz for same-chassis; inter-device default is
  // DEFAULT_MIN_FREQ_GAP_MHZ (20 MHz). Set same-chassis to 0 to fall
  // back to the inter-device floor (legacy single-floor mode).
  // Global same-chassis floor is derived from the per-role values
  // (max of the two) — no separate UI control needed.
  // Channel-step global accept gate: drop the entire channel plan when
  // its relative reduction in total weighted conflict severity is below
  // this fraction. Stops near-zero-improvement plans that produce
  // visible churn across consecutive runs. 0.05 = 5 %; 0 disables.
  const DEFAULT_CHANNEL_GLOBAL_ACCEPT_FLOOR = 0.05;
  const [channelGlobalAcceptFloor, setChannelGlobalAcceptFloor] = useState(DEFAULT_CHANNEL_GLOBAL_ACCEPT_FLOOR);
  // Anti-oscillation cool-down: how long a radio just changed by an
  // optimize plan stays "sticky" — the next plan requires a stricter
  // per-action improvement floor before moving it again. 0 disables.
  const DEFAULT_CHANNEL_STICKY_COOLDOWN_MIN = 60;
  const [channelStickyCooldownMin, setChannelStickyCooldownMin] = useState(DEFAULT_CHANNEL_STICKY_COOLDOWN_MIN);
  const [neighborRssiFloorDbm, setNeighborRssiFloorDbm] = useState(DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM);
  const [regulatoryDomain, setRegulatoryDomain] = useState('default');
  // cap_only_actions: when true, only CAP radios receive channel
  // actions; Node AP radios stay frozen as constraints. Default false.
  const [capOnlyActions, setCapOnlyActions] = useState(false);
  // excludeIdleAPs: when true (default), idle AP graph trimming stays
  // enabled (backend idle_ap_retry_disabled=false). Uncheck to keep
  // unused AP devices in the heard/conflict graph.
  const [excludeIdleAPs, setExcludeIdleAPs] = useState(true);
  const [channelAllowExpanded, setChannelAllowExpanded] = useState(false);
  const [channelGroupIds, setChannelGroupIds] = useState([]);
  const [channelAllowList5GHz, setChannelAllowList5GHz] = useState('');
  const [channelAllowList6GHz, setChannelAllowList6GHz] = useState('');

  // Per-role channel planning (Jun 2026). CAP backhaul plans at 40 MHz
  // with a 40 MHz same-chassis floor; node/hop treemgmt radios plan at
  // 20 MHz with a 20 MHz floor. Channel-fixing, segment awareness and
  // the two radio-exclusion guards all default ON to match the backend.
  const DEFAULT_CAP_CHANNEL_BANDWIDTH = 40;
  const DEFAULT_HOP_CHANNEL_BANDWIDTH = 20;
  const DEFAULT_CAP_SAME_CHASSIS_MIN_SEP_MHZ = 40;
  const DEFAULT_HOP_SAME_CHASSIS_MIN_SEP_MHZ = 20;
  const [capChannelBandwidth, setCapChannelBandwidth] = useState(DEFAULT_CAP_CHANNEL_BANDWIDTH);
  const [hopChannelBandwidth, setHopChannelBandwidth] = useState(DEFAULT_HOP_CHANNEL_BANDWIDTH);
  const channelBandwidth = Math.max(capChannelBandwidth, hopChannelBandwidth);
  const [capSameChassisMinSep, setCapSameChassisMinSep] = useState(DEFAULT_CAP_SAME_CHASSIS_MIN_SEP_MHZ);
  const [hopSameChassisMinSep, setHopSameChassisMinSep] = useState(DEFAULT_HOP_SAME_CHASSIS_MIN_SEP_MHZ);
  const [fixChannelGroups, setFixChannelGroups] = useState(true);
  const [segmentAware, setSegmentAware] = useState(true);
  const [excludePublicWifiRadios, setExcludePublicWifiRadios] = useState(true);
  const [excludeMaxChildrenZeroRadios, setExcludeMaxChildrenZeroRadios] = useState(true);

  const channelAllowActive = useMemo(
    () => channelGroupIds.length > 0
      || String(channelAllowList5GHz).trim() !== ''
      || String(channelAllowList6GHz).trim() !== '',
    [channelGroupIds, channelAllowList5GHz, channelAllowList6GHz],
  );

  // ---- Power-stage tunables ---------------------------------------
  //
  // PowerImprovementFloorDB: chained-mode minimum dB change a power
  // action must clear before being emitted. 3 matches the backend
  // default; lower values surface smaller adjustments.
  const DEFAULT_POWER_IMPROVEMENT_FLOOR_DB = 3;
  const [powerImprovementFloorDb, setPowerImprovementFloorDb] = useState(DEFAULT_POWER_IMPROVEMENT_FLOOR_DB);

  // PowerUplinkSeverityWeight: 1.0 = symmetric (default), 1.5 = legacy
  // edge-TX-dominant bias. Operators that want to surface uplink
  // recommendations more prominently can opt back into 1.5.
  const DEFAULT_POWER_UPLINK_SEVERITY_WEIGHT = 1.0;
  const [powerUplinkSeverityWeight, setPowerUplinkSeverityWeight] = useState(DEFAULT_POWER_UPLINK_SEVERITY_WEIGHT);

  // PowerRegulatoryDomain selects the EIRP cap table. "default" applies
  // no extra restriction beyond the 18 dBm hardware cap; "eu" / "il" /
  // "us" clamp via (EIRP_cap − antenna_gain).
  const [powerRegulatoryDomain, setPowerRegulatoryDomain] = useState('default');

  // Antenna gain overrides. Defaults match the Juganu fleet — CAP
  // directional 11 dBi, Node omni 0 dBi. Only consulted when
  // PowerRegulatoryDomain ≠ "default".
  const DEFAULT_POWER_CAP_ANTENNA_GAIN_DBI = 11;
  const DEFAULT_POWER_NODE_ANTENNA_GAIN_DBI = 0;
  const [powerCapAntennaGainDbi, setPowerCapAntennaGainDbi] = useState(DEFAULT_POWER_CAP_ANTENNA_GAIN_DBI);
  const [powerNodeAntennaGainDbi, setPowerNodeAntennaGainDbi] = useState(DEFAULT_POWER_NODE_ANTENNA_GAIN_DBI);

  // ChannelImprovementFloor: chained-mode minimum severity drop a
  // channel action must clear. 0.05 matches the backend default.
  const DEFAULT_CHANNEL_IMPROVEMENT_FLOOR = 0.05;
  const [channelImprovementFloor, setChannelImprovementFloor] = useState(DEFAULT_CHANNEL_IMPROVEMENT_FLOOR);

  // Algo step selection — all enabled by default (= meta behaviour).
  const [enableTopology, setEnableTopology] = useState(true);
  const [enablePower, setEnablePower] = useState(true);
  const [enableChannel, setEnableChannel] = useState(true);
  const [debugInputAudit, setDebugInputAudit] = useState(false);

  // Iperf-anchored optimize state.
  // - useIperf defaults to ON: when a matching site-wide iperf run
  //   exists, the planner anchors topology / channel / power / score
  //   on real measurements. When no match exists we still let the
  //   operator submit; the planner falls back to today's theoretical
  //   estimates and surfaces a warning banner.
  // - iperfRunId='' means "let the server auto-pick the
  //   highest-matching run" — common case.
  // - matchingIperfRuns is loaded async; null = haven't fetched yet,
  //   [] = fetched but nothing qualifies.
  const [useIperf, setUseIperf] = useState(true);
  const [iperfRunId, setIperfRunId] = useState('');
  const [matchingIperfRuns, setMatchingIperfRuns] = useState(null);
  const iperfAutoPickPerSegment = useMemo(
    () => shouldAutoPickIperfPerSegment({ optimizeScopeIsSiteWide }),
    [optimizeScopeIsSiteWide],
  );

  const iperfScopeKey = useMemo(
    () => JSON.stringify({
      site: optimizeScopeIsSiteWide,
      mode: scopeMode,
      caps: selectedCapSerials,
      trees: selectedCustomTreeIds,
      devices: mapFocusSerials,
    }),
    [
      optimizeScopeIsSiteWide,
      scopeMode,
      selectedCapSerials,
      selectedCustomTreeIds,
      mapFocusSerials,
    ],
  );

  useEffect(() => {
    setIperfRunId('');
  }, [iperfScopeKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const runs = await api.listIperfTestRunsMatching(iperfMatchingScope);
        if (!cancelled) {
          const loaded = (Array.isArray(runs) ? runs : [])
            .filter((run) => isRrmEligibleIperfRun(run, { scopedOptimize: !optimizeScopeIsSiteWide }));
          setMatchingIperfRuns(loaded);
          setIperfRunId((prev) => {
            if (loaded.length === 0) return '';
            if (!optimizeScopeIsSiteWide) return '';
            if (prev && loaded.some((run) => run.id === prev)) return prev;
            return loaded[0].id;
          });
        }
      } catch (err) {
        if (!cancelled) setMatchingIperfRuns([]);
      }
    })();
    return () => { cancelled = true; };
  }, [iperfMatchingScope, optimizeScopeIsSiteWide]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getCustomTrees();
        if (cancelled) return;
        setCustomTrees(Array.isArray(data?.trees) ? data.trees : []);
      } catch {
        if (!cancelled) setCustomTrees([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const runOptimize = useCallback(async () => {
    if (scopeMode === 'trees' && selectedCustomTreeIds.length === 0) {
      enqueueSnackbar('Select at least one custom tree in Optimize scope.', { variant: 'warning' });
      return;
    }
    if (scopeMode === 'caps' && selectedCapSerials.length === 0) {
      enqueueSnackbar('Select at least one CAP in Optimize scope.', { variant: 'warning' });
      return;
    }

    let channelAllowLists;
    if (String(channelAllowList5GHz).trim() || String(channelAllowList6GHz).trim()) {
      channelAllowLists = {};
      if (String(channelAllowList5GHz).trim()) {
        const parsed5 = parseTreemgmtChannelList(channelAllowList5GHz);
        if (parsed5.error) {
          enqueueSnackbar(`5 GHz channel list: ${parsed5.error}`, { variant: 'warning' });
          return;
        }
        channelAllowLists['5GHz'] = parsed5.channels;
      }
      if (String(channelAllowList6GHz).trim()) {
        const parsed6 = parseTreemgmtChannelList(channelAllowList6GHz);
        if (parsed6.error) {
          enqueueSnackbar(`6 GHz channel list: ${parsed6.error}`, { variant: 'warning' });
          return;
        }
        channelAllowLists['6GHz'] = parsed6.channels;
      }
    }

    setLoading(true);
    const enabledSteps = [];
    if (enableTopology) enabledSteps.push('topology');
    if (enablePower) enabledSteps.push('power');
    if (enableChannel) enabledSteps.push('channel');
    const body = {
      max_iterations: maxIterations,
      use_health_focus: useHealthFocus,
      rebuild_tree: rebuildTree,
      channel_bandwidth: channelBandwidth,
      threshold,
      min_frequency_separation_mhz: minFrequencySeparationMHz,
      same_chassis_min_frequency_separation_mhz: OPTIMIZE_GLOBAL_SAME_CHASSIS_MIN_SEP_MHZ,
      channel_global_accept_floor: channelGlobalAcceptFloor,
      channel_stickiness_cooldown_minutes: channelStickyCooldownMin,
      neighbor_rssi_floor_dbm: neighborRssiFloorDbm,
      regulatory_domain: regulatoryDomain,
      cap_only_actions: capOnlyActions,
      ...(excludeIdleAPs ? {} : { idle_ap_retry_disabled: true }),
      use_iperf: useIperf,
      iperf_run_id: useIperf ? iperfRunId : '',
      // Empty array = all steps (backend default). Sending specific
      // steps restricts the loop to only those.
      enabled_steps: enabledSteps.length === 3 ? [] : enabledSteps,
      debug: debugInputAudit,
      ...(improvementFloor !== DEFAULT_IMPROVEMENT_FLOOR
        && Number.isFinite(improvementFloor)
        && improvementFloor > 0
        ? { improvement_floor: improvementFloor }
        : {}),
      // Power tunables. Only sent when they differ from the backend
      // defaults so the response stays a no-op for operators that
      // didn't touch the form.
      ...(powerImprovementFloorDb !== DEFAULT_POWER_IMPROVEMENT_FLOOR_DB
        && Number.isFinite(powerImprovementFloorDb)
        && powerImprovementFloorDb >= 0
        ? { power_improvement_floor_db: powerImprovementFloorDb }
        : {}),
      ...(powerUplinkSeverityWeight !== DEFAULT_POWER_UPLINK_SEVERITY_WEIGHT
        && Number.isFinite(powerUplinkSeverityWeight)
        && powerUplinkSeverityWeight > 0
        ? { power_uplink_severity_weight: powerUplinkSeverityWeight }
        : {}),
      ...(powerRegulatoryDomain && powerRegulatoryDomain !== 'default'
        ? { power_regulatory_domain: powerRegulatoryDomain }
        : {}),
      ...(powerCapAntennaGainDbi !== DEFAULT_POWER_CAP_ANTENNA_GAIN_DBI
        && Number.isFinite(powerCapAntennaGainDbi)
        ? { power_cap_antenna_gain_dbi: powerCapAntennaGainDbi }
        : {}),
      ...(powerNodeAntennaGainDbi !== DEFAULT_POWER_NODE_ANTENNA_GAIN_DBI
        && Number.isFinite(powerNodeAntennaGainDbi)
        ? { power_node_antenna_gain_dbi: powerNodeAntennaGainDbi }
        : {}),
      // Channel improvement floor: chained-mode per-action gate.
      ...(channelImprovementFloor !== DEFAULT_CHANNEL_IMPROVEMENT_FLOOR
        && Number.isFinite(channelImprovementFloor)
        && channelImprovementFloor >= 0
        ? { channel_improvement_floor: channelImprovementFloor }
        : {}),
      ...(reparentWhitelistCount !== DEFAULT_REPARENT_WHITELIST_COUNT
        && Number.isFinite(reparentWhitelistCount)
        && reparentWhitelistCount >= 1
        ? { reparent_whitelist_count: reparentWhitelistCount }
        : {}),
      ...(scopeMode === 'trees' && selectedCustomTreeIds.length === 1 ? { custom_tree_id: selectedCustomTreeIds[0] } : {}),
      ...(scopeMode === 'trees' && selectedCustomTreeIds.length > 1 ? { custom_tree_ids: selectedCustomTreeIds } : {}),
      ...(channelGroupIds.length > 0 ? { channel_group_ids: channelGroupIds } : {}),
      ...(channelAllowLists ? { channel_allow_lists: channelAllowLists } : {}),
      // Per-role channel planning. Per-role bandwidth / same-chassis
      // floors are only sent when they differ from the role defaults;
      // the feature toggles are only sent when turned OFF (backend
      // defaults them ON).
      ...(capChannelBandwidth !== DEFAULT_CAP_CHANNEL_BANDWIDTH ? { cap_channel_bandwidth: capChannelBandwidth } : {}),
      ...(hopChannelBandwidth !== DEFAULT_HOP_CHANNEL_BANDWIDTH ? { hop_channel_bandwidth: hopChannelBandwidth } : {}),
      ...(capSameChassisMinSep !== DEFAULT_CAP_SAME_CHASSIS_MIN_SEP_MHZ
        ? { cap_same_chassis_min_frequency_separation_mhz: capSameChassisMinSep }
        : {}),
      ...(hopSameChassisMinSep !== DEFAULT_HOP_SAME_CHASSIS_MIN_SEP_MHZ
        ? { hop_same_chassis_min_frequency_separation_mhz: hopSameChassisMinSep }
        : {}),
      ...(fixChannelGroups ? {} : { fix_channel_groups: false }),
      ...(segmentAware ? {} : { segment_aware: false }),
      ...(excludePublicWifiRadios ? {} : { exclude_public_wifi_radios: false }),
      ...(excludeMaxChildrenZeroRadios ? {} : { exclude_max_children_zero_radios: false }),
      ...buildOptimizeScopePayload(scopeMode, { selectedCapSerials, mapFocusSerials }),
    };
    const outcome = await runGuarded(() => api.runOptimize(body));
    setLoading(false);
    if (outcome.ok) {
      clearMissing();
      setResult(outcome.data);
      enqueueSnackbar('Optimization plan ready.', { variant: 'success' });
      return;
    }
    if (outcome.gated) {
      setResult(null);
      enqueueSnackbar('Optimize cannot run: neighbour data is incomplete.', { variant: 'error' });
      return;
    }
    enqueueSnackbar(outcome.error?.message || 'Failed to run optimize', { variant: 'error' });
  }, [
    maxIterations, useHealthFocus, rebuildTree, threshold, improvementFloor,
    minFrequencySeparationMHz, channelGlobalAcceptFloor, channelStickyCooldownMin,
    neighborRssiFloorDbm, regulatoryDomain, capOnlyActions, excludeIdleAPs,
    powerImprovementFloorDb, powerUplinkSeverityWeight, powerRegulatoryDomain,
    powerCapAntennaGainDbi, powerNodeAntennaGainDbi, channelImprovementFloor,
    reparentWhitelistCount, selectedCustomTreeIds, scopeMode, selectedCapSerials, mapFocusSerials,
    useIperf, iperfRunId, enableTopology, enablePower, enableChannel, debugInputAudit,
    channelGroupIds, channelAllowList5GHz, channelAllowList6GHz,
    channelBandwidth, capChannelBandwidth, hopChannelBandwidth, capSameChassisMinSep, hopSameChassisMinSep,
    fixChannelGroups, segmentAware, excludePublicWifiRadios, excludeMaxChildrenZeroRadios,
    enqueueSnackbar, runGuarded, clearMissing,
  ]);

  const handleScanMissing = useCallback(async () => {
    const summary = await scanMissing();
    if (!summary) return;
    enqueueSnackbar(
      summary.failed === 0
        ? `Triggered neighbour scan for ${summary.accepted} device(s).`
        : `Triggered ${summary.accepted} scan(s); ${summary.failed} failed to start.`,
      { variant: summary.failed === 0 ? 'info' : 'warning' },
    );
    refreshDataStatus();
  }, [scanMissing, enqueueSnackbar, refreshDataStatus]);

  const allActions = useMemo(() => flattenAllActions(result?.steps), [result]);
  const uplinkUsage = useMemo(() => summarizeUplinkUsage(result), [result]);

  // Build a `createPlanRun` payload from a list of analyser entries.
  // The backend will dispatch every action and reconcile the device
  // state reports against per-action expectations — we don't need to
  // walk the actions client-side any more.
  const buildPlanPayload = useCallback((entries, name, source, persistTree = false) => {
    const planChannelGroupBandLabel = (band) => {
      const b = String(band || '').toLowerCase().trim();
      if (b === '2.4ghz' || b === '2.4' || b === '1') return '2.4GHz';
      if (b === '5ghz' || b === '5' || b === '2') return '5GHz';
      if (b === '6ghz' || b === '6' || b === '3') return '6GHz';
      if (b.includes('6') || b.includes('7')) return '6GHz';
      if (b.includes('5')) return '5GHz';
      return '';
    };
    const actions = entries.map((entry) => {
      const a = entry.action || {};
      const payload = { ...(a.payload || {}) };
      if (!payload.serial_number && a.serial) payload.serial_number = a.serial;
      if (!payload.band && a.band) payload.band = a.band;
      if (!payload.tree_id && a.tree_id) payload.tree_id = a.tree_id;
      if (payload.channel == null && a.proposed_channel != null) payload.channel = a.proposed_channel;
      if (payload.current_channel == null && a.current_channel != null) payload.current_channel = a.current_channel;
      if (a.groups_only) payload.groups_only = true;
      if (!payload.channel_groups && Array.isArray(a.proposed_channel_group) && a.proposed_channel_group.length > 0) {
        const bandLabel = planChannelGroupBandLabel(payload.band || a.band);
        if (bandLabel === '5GHz' || bandLabel === '6GHz') {
          payload.channel_groups = { [bandLabel]: a.proposed_channel_group };
        }
      }
      // For set-parent actions we only stamp `tolerant` when the
      // operator explicitly disabled it via the checkbox. Leaving
      // it undefined lets the runner apply its own default
      // (tolerant=true) so an outdated analyser response from a
      // pre-flag backend still gets the new behaviour.
      const isSetParent = (entry.kind === 'set-parent')
        || (a.endpoint || '').endsWith('/set-parent');
      if (isSetParent && tolerantReparent === false) {
        payload.tolerant = false;
      }
      return {
        action_id: a.action_id,
        serial: a.serial || a.payload?.serial_number,
        kind: entry.kind, // hint; backend normalises further from endpoint
        endpoint: a.endpoint,
        method: a.method || 'POST',
        payload,
        description: a.reason || a.description,
      };
    }).filter((a) => a.serial && a.endpoint);
    return { name, source, actions, persist_tree: !!persistTree };
  }, [tolerantReparent]);

  // Surface the most useful 409 details (which serial blocked us +
  // the in-flight plan id) so the operator can jump straight to the
  // already-running plan instead of guessing what's wrong.
  const surfacePlanError = useCallback(async (err, fallback) => {
    const data = err?.data || err?.response;
    if (err?.status === 409 || err?.statusCode === 409) {
      const planId = data?.plan_id;
      const serial = data?.serial;
      const message = serial && planId
        ? `Another plan is already running for ${serial}. Open it from the Plans tab.`
        : 'Another plan is already running for one of the targeted devices.';
      enqueueSnackbar(message, { variant: 'warning' });
      if (planId && typeof onOpenPlan === 'function') onOpenPlan(planId);
      return;
    }
    enqueueSnackbar(err?.message || fallback, { variant: 'error' });
  }, [enqueueSnackbar, onOpenPlan]);

  const applyEntirePlan = useCallback(async () => {
    if (allActions.length === 0) return;
    setApplyAllBusy(true);
    setApplyProgress({ done: 0, total: allActions.length });
    try {
      const payload = buildPlanPayload(
        allActions,
        `Optimize plan ${new Date().toLocaleString()}`,
        'analyser-meta',
        planPersistTree,
      );
      if (payload.actions.length === 0) {
        enqueueSnackbar('No applicable actions to dispatch.', { variant: 'warning' });
        return;
      }
      const plan = await api.createPlanRun(payload);
      setApplyProgress({ done: plan.summary?.verified || 0, total: plan.summary?.total || payload.actions.length });
      enqueueSnackbar(
        `Plan queued: ${plan.actions?.length || payload.actions.length} action(s). Dispatching in the background; live progress is in the Plans tab.`,
        { variant: 'success' },
      );
      if (typeof onOpenPlan === 'function' && plan?.id) onOpenPlan(plan.id);
    } catch (error) {
      await surfacePlanError(error, 'Failed to dispatch plan');
    } finally {
      setApplyAllBusy(false);
    }
  }, [allActions, buildPlanPayload, enqueueSnackbar, surfacePlanError, onOpenPlan, planPersistTree]);

  const applyOneStep = useCallback(async (entries, label) => {
    if (!entries?.length) return;
    setApplyAllBusy(true);
    try {
      const payload = buildPlanPayload(
        entries,
        `${label} ${new Date().toLocaleString()}`,
        `analyser-${label.toLowerCase().replace(/\s+/g, '-')}`,
        planPersistTree,
      );
      if (payload.actions.length === 0) {
        enqueueSnackbar(`${label}: nothing to dispatch.`, { variant: 'warning' });
        return;
      }
      const plan = await api.createPlanRun(payload);
      enqueueSnackbar(
        `${label}: queued ${plan.actions?.length || payload.actions.length} action(s). Dispatching in the background; live progress is in the Plans tab.`,
        { variant: 'success' },
      );
      if (typeof onOpenPlan === 'function' && plan?.id) onOpenPlan(plan.id);
    } catch (error) {
      await surfacePlanError(error, `${label}: failed to dispatch`);
    } finally {
      setApplyAllBusy(false);
    }
  }, [buildPlanPayload, enqueueSnackbar, surfacePlanError, onOpenPlan, planPersistTree]);

  const composite = result?.composite_fitness;
  const compositeScore = composite?.score;
  const compositeTone = compositeRingColor(compositeScore);

  const headerRight = (
    <Stack direction="row" spacing={1} alignItems="center">
      <NeighborDataChipConnected />
      <Tooltip title={paramsOpen ? 'Hide parameters' : 'Tune parameters'}>
        <Button
          size="small"
          variant={paramsOpen ? 'contained' : 'outlined'}
          startIcon={<TuneIcon />}
          onClick={() => setParamsOpen((v) => !v)}
        >
          Parameters
        </Button>
      </Tooltip>
    </Stack>
  );

  // ---- Render -----------------------------------------------------------

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Full-panel planning overlay — shown while the async job runs */}
      <Backdrop
        open={loading}
        sx={{
          position: 'absolute',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          borderRadius: 1,
          bgcolor: 'rgba(0, 0, 0, 0.45)',
          backdropFilter: 'blur(2px)',
        }}
      >
        <Fade in={loading}>
          <Stack alignItems="center" spacing={2.5}>
            <CircularProgress size={64} thickness={3.5} sx={{ color: 'common.white' }} />
            <Stack alignItems="center" spacing={0.5}>
              <Typography variant="h6" sx={{ color: 'common.white', fontWeight: 600 }}>
                Planning in progress
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                Loading network data and computing plan…
              </Typography>
            </Stack>
          </Stack>
        </Fade>
      </Backdrop>

      <AnalyzerHeader
        catalog={catalog}
        loading={loading}
        onRun={runOptimize}
        hasResult={!!result}
        rightContent={headerRight}
        // The chained-pipeline diagram lives inside AnalyzerHeader's
        // "How it works" dropdown (alongside the standard algorithm /
        // measures / acts-on summary) so all "what does this do?"
        // content is in one discoverable place instead of permanently
        // taking up panel space above the result.
        extraHelpContent={<MetaPipelineView />}
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
          Optimize scope
        </Typography>
        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="optimize-scope-mode-label" shrink>
              Scope type
            </InputLabel>
            <Select
              labelId="optimize-scope-mode-label"
              value={scopeMode}
              onChange={handleScopeModeChange}
              label="Scope type"
            >
              <MenuItem value="site">All site</MenuItem>
              <MenuItem value="caps">CAP trees</MenuItem>
              <MenuItem value="trees">Custom trees</MenuItem>
              <MenuItem value="devices" disabled={mapFocusSerials.length === 0}>
                Map selection
              </MenuItem>
            </Select>
          </FormControl>

          {scopeMode === 'site' && (
            <Chip color="default" variant="outlined" label={scopeSummary} sx={{ mt: 0.5 }} />
          )}

          {scopeMode === 'devices' && (
            <>
              <Chip color="primary" variant="outlined" label={scopeSummary} sx={{ mt: 0.5 }} />
              <Button size="small" onClick={clearDeviceScope} sx={{ mt: 0.25 }}>
                Clear map selection
              </Button>
            </>
          )}

          {scopeMode === 'caps' && (
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel id="optimize-cap-scope-label" shrink>
                CAP scope
              </InputLabel>
              <Select
                labelId="optimize-cap-scope-label"
                multiple
                displayEmpty
                value={selectedCapSerials}
                onChange={handleCapScopeChange}
                label="CAP scope"
                renderValue={(selected) => {
                  if (!selected?.length) return 'Select CAP(s)';
                  if (caps.length > 1 && selected.length === caps.length) {
                    return 'All Site';
                  }
                  if (selected.length === 1) return selected[0];
                  return `${selected.length} CAP(s)`;
                }}
              >
                {caps.length > 1 && (
                  <MenuItem value="__all__">
                    <Checkbox
                      checked={selectedCapSerials.length === caps.length && caps.length > 0}
                      indeterminate={
                        selectedCapSerials.length > 0 && selectedCapSerials.length < caps.length
                      }
                    />
                    <ListItemText primary="All Site" />
                  </MenuItem>
                )}
                {caps.map((d) => (
                  <MenuItem key={d.serial_number} value={d.serial_number}>
                    <Checkbox checked={selectedCapSerials.indexOf(d.serial_number) > -1} />
                    <ListItemText
                      primary={d.serial_number}
                      secondary={d.name && d.name !== d.serial_number ? d.name : undefined}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {scopeMode === 'trees' && (
            <Autocomplete
              multiple
              size="small"
              sx={{ minWidth: 320, flex: 1 }}
              options={customTrees}
              value={selectedCustomTrees}
              onChange={handleCustomTreeScopeChange}
              getOptionLabel={(option) => option.name || option.id}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Custom trees"
                  placeholder={customTrees.length === 0 ? 'No segments defined' : 'Select one or more'}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name || option.id}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2">{option.name || option.id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.segment_id != null ? `Segment ${option.segment_id}` : 'No segment ID'}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {scopeHelpText}
        </Typography>
      </Paper>

      {missingSerials.length > 0 && (
        <NeighborDataIncompleteCard
          missingSerials={missingSerials}
          onScanMissing={handleScanMissing}
          scanningSerials={scanningSerials}
        />
      )}

      {/* Parameters drawer — neutral surface, matches the panel
          background so it integrates with the rest of the analyser
          UI instead of standing out with a contrast colour. */}
      <Collapse in={paramsOpen}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
            Parameters
          </Typography>

          <Stack spacing={0.75}>
            {/* General */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { mt: 0 },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  General
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} md={2}>
                    <NumericParamField
                      label="Max iterations"
                      size="small"
                      fullWidth
                      value={maxIterations}
                      onChange={setMaxIterations}
                      fallback={3}
                      min={1}
                      max={10}
                      parse={parseIntField}
                      inputProps={{ step: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 1 }}>
                        Algorithms:
                      </Typography>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={enableTopology} onChange={(e) => setEnableTopology(e.target.checked)} />}
                        label={<Typography variant="caption">Topology</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={enablePower} onChange={(e) => setEnablePower(e.target.checked)} />}
                        label={<Typography variant="caption">TX Power</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={enableChannel} onChange={(e) => setEnableChannel(e.target.checked)} />}
                        label={<Typography variant="caption">Channels</Typography>}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        Health + Backup always run (advisory)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip
                      arrow
                      placement="top"
                      title="Restrict the plan to devices the Health analyser flagged with active issues (link-retry bursts, weak uplink RSSI, low MCS, etc.). Topology, power and channel stages will only consider those devices and their immediate neighbours, producing a smaller, faster plan that targets the worst-performing parts of the mesh first. Leave off to plan across all devices."
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={useHealthFocus}
                            onChange={(e) => setUseHealthFocus(e.target.checked)}
                          />
                        }
                        label={<Typography variant="caption">Focus on Health hits</Typography>}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip
                      arrow
                      placement="top"
                      title="Embed a full input audit in the response: per-device fields, golden neighbor RSSI (STA/AP), ACS samples used for DSATUR channel scoring, and graph parent candidates. Large payload — for algo debugging only."
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={debugInputAudit}
                            onChange={(e) => setDebugInputAudit(e.target.checked)}
                          />
                        }
                        label={<Typography variant="caption">Debug input audit</Typography>}
                      />
                    </Tooltip>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Topology */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { mt: 0 },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Topology
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} md={2}>
                    <Tooltip title="Reparenting commit threshold (improvement_floor): minimum per-edge tree-cost improvement required before a set-parent action appears in the plan. The analyser still explores moves down to 0.5 internally. Uses effective link cost (measured uplink when available), not scan RSSI alone. Default 1.0 when omitted; lower values (e.g. 0.3) ship smaller gains that search already found.">
                      <NumericParamField
                        label="Reparent floor"
                        size="small"
                        fullWidth
                        value={improvementFloor}
                        onChange={setImprovementFloor}
                        fallback={DEFAULT_IMPROVEMENT_FLOOR}
                        min={0.1}
                        inputProps={{ step: 0.1 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Tooltip title="How many parent serials to whitelist on each reparent action (primary + Yen-ranked alternates). Excludes the current bad parent. Default 3, max 5.">
                      <NumericParamField
                        label="Whitelist parents"
                        size="small"
                        fullWidth
                        value={reparentWhitelistCount}
                        onChange={setReparentWhitelistCount}
                        fallback={DEFAULT_REPARENT_WHITELIST_COUNT}
                        min={1}
                        max={5}
                        parse={parseIntField}
                        inputProps={{ step: 1 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip
                      arrow
                      placement="top"
                      title="Plan as if this were a first-deployment / clean-slate build of the mesh tree. Lifts the safety caps that normally limit the size of a single re-parent's blast radius and the number of re-parents per plan, allowing the planner to return a complete topology rebuild. Use only when you intend to apply the plan during a maintenance window — large sub-trees may briefly disconnect."
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={rebuildTree}
                            onChange={(e) => setRebuildTree(e.target.checked)}
                            color="warning"
                          />
                        }
                        label={<Typography variant="caption">Rebuild whole tree (clean slate)</Typography>}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: useIperf ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        px: 1.5,
                        py: 1,
                        transition: 'border-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Tooltip
                        arrow
                        placement="top"
                        title={optimizeScopeIsSiteWide
                          ? 'When on, the planner anchors its decisions on a previously-run full site-wide iperf session. Eligible runs must include both every-device-to-parent and leaf-to-CAP tests. The match % tells you how much of the run\'s data applies.'
                          : iperfAutoPickPerSegment
                            ? 'When on, auto-pick merges the best iperf run per CAP/segment in scope (leave the picker on Auto). Each custom tree usually has its own stored run.'
                            : 'When on, the planner anchors on iperf measurements that overlap the selected optimize scope — site-wide runs or single-CAP runs for CAPs in scope. Match % is computed within the scope only.'}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={useIperf}
                              onChange={(e) => {
                                setUseIperf(e.target.checked);
                                if (!e.target.checked) setIperfRunId('');
                              }}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                              Use iperf measurements
                            </Typography>
                          }
                          sx={{ mr: 0 }}
                        />
                      </Tooltip>
                      <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
                      <Tooltip
                        arrow
                        placement="top"
                        title={optimizeScopeIsSiteWide
                          ? 'Completed site-wide iperf runs with both hop and leaf-to-CAP tests, sorted by match %. Partial runs are filtered out.'
                          : iperfAutoPickPerSegment
                            ? 'Each segment\'s best matching run is used when Auto is selected. Pick one run below to anchor on a single segment only.'
                            : 'Completed iperf runs that overlap the selected scope (site-wide or matching CAP trees), sorted by scoped match %.'}
                      >
                        <FormControl
                          size="small"
                          sx={{ flex: 1, minWidth: 220 }}
                          disabled={!useIperf || !matchingIperfRuns?.length}
                        >
                          <InputLabel shrink>Iperf run</InputLabel>
                          <Select
                            label="Iperf run"
                            notched
                            displayEmpty
                            value={iperfRunId}
                            onChange={(e) => setIperfRunId(e.target.value)}
                          >
                            <MenuItem value="">
                              <em>{iperfAutoPickPerSegment ? 'Auto — best run per segment' : 'Auto-pick newest match'}</em>
                            </MenuItem>
                            {(matchingIperfRuns || []).map((run) => {
                              const pct = Math.round((run.match_score || 0) * 100);
                              const completed = run.completed_at
                                ? new Date(run.completed_at).toLocaleString()
                                : 'unknown date';
                              const capLabel = run.cap_serial && !run.is_site_wide
                                ? ` · ${run.cap_serial}`
                                : '';
                              return (
                                <MenuItem key={run.id} value={run.id}>
                                  {run.name || run.id?.slice(0, 8) || 'iperf run'}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                  >
                                    {pct}% match{capLabel} · {completed}
                                  </Typography>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Tooltip>
                      {useIperf && matchingIperfRuns === null && (
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          Loading…
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  {useIperf && Array.isArray(matchingIperfRuns) && matchingIperfRuns.length === 0 && (
                    <Grid item xs={12}>
                      <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                        {optimizeScopeIsSiteWide
                          ? 'No completed site-wide iperf run overlaps the current tree. Optimize will use theoretical estimates only.'
                          : 'No completed iperf run overlaps the selected scope. Optimize will use theoretical estimates only.'}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Channels */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { mt: 0 },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Channels
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} md={2}>
                    <Tooltip title="Channel analyser greediness — lower = more channel actions (not reparenting)">
                      <NumericParamField
                        label="Channel threshold"
                        size="small"
                        fullWidth
                        value={threshold}
                        onChange={setThreshold}
                        fallback={0.3}
                        min={0}
                        max={1}
                        inputProps={{ step: 0.1 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Tooltip title={`Minimum center-frequency separation enforced between INTER-DEVICE APs that hear each other (scan RSSI ≥ floor below). Default ${DEFAULT_MIN_FREQ_GAP_MHZ} MHz.`}>
                      <NumericParamField
                        label="Min freq gap (MHz)"
                        size="small"
                        fullWidth
                        value={minFrequencySeparationMHz}
                        onChange={setMinFrequencySeparationMHz}
                        fallback={DEFAULT_MIN_FREQ_GAP_MHZ}
                        min={20}
                        inputProps={{ step: 20 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Tooltip title="Drop the channel plan unless its relative reduction in total weighted conflict severity is at least this fraction. Stops the planner from emitting near-zero-improvement actions that cause visible churn. 0.05 = 5 %. Set to 0 (or negative) to disable.">
                      <NumericParamField
                        label="Channel accept floor"
                        size="small"
                        fullWidth
                        value={channelGlobalAcceptFloor}
                        onChange={setChannelGlobalAcceptFloor}
                        fallback={DEFAULT_CHANNEL_GLOBAL_ACCEPT_FLOOR}
                        min={0}
                        max={1}
                        inputProps={{ step: 0.01 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Tooltip title="Chained-mode minimum severity drop a channel action must clear before it surfaces. 0.05 (5 %) is the backend default. Lower values surface smaller wins.">
                      <NumericParamField
                        label="Channel floor"
                        size="small"
                        fullWidth
                        value={channelImprovementFloor}
                        onChange={setChannelImprovementFloor}
                        fallback={DEFAULT_CHANNEL_IMPROVEMENT_FLOOR}
                        min={0}
                        max={1}
                        inputProps={{ step: 0.01 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Tooltip title="Post-plan cool-down (minutes) for radios just moved by the optimizer. The next plan applies a 2× improvement floor before moving them again, which breaks low-amplitude oscillation between two locally-optimal DSATUR assignments. 0 disables. State is in-process and is reset by a backend restart.">
                      <NumericParamField
                        label="Sticky radio cooldown (min)"
                        size="small"
                        fullWidth
                        value={channelStickyCooldownMin}
                        onChange={setChannelStickyCooldownMin}
                        fallback={DEFAULT_CHANNEL_STICKY_COOLDOWN_MIN}
                        min={0}
                        inputProps={{ step: 5 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Tooltip title={`Neighbour scan RSSI floor (dBm). Observations weaker than this do not count as heard for channel constraints. Default ${DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM}.`}>
                      <NumericParamField
                        label="Heard RSSI floor"
                        size="small"
                        fullWidth
                        value={neighborRssiFloorDbm}
                        onChange={setNeighborRssiFloorDbm}
                        fallback={DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM}
                        max={0}
                        inputProps={{ step: 1 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Reg. domain</InputLabel>
                      <Select
                        label="Reg. domain"
                        value={regulatoryDomain}
                        onChange={(e) => setRegulatoryDomain(e.target.value)}
                      >
                        <MenuItem value="default">Default</MenuItem>
                        <MenuItem value="dfs_allowed">DFS allowed</MenuItem>
                        <MenuItem value="dfs_blocked">DFS blocked</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        onClick={() => setChannelAllowExpanded((v) => !v)}
                        sx={{ cursor: 'pointer', p: 1.5, pb: channelAllowExpanded ? 0.75 : 1.5, userSelect: 'none' }}
                      >
                        <IconButton size="small" tabIndex={-1}>
                          {channelAllowExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                        </IconButton>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              DSATUR allowed channels
                            </Typography>
                            {channelAllowActive && (
                              <Chip label="Restricted" size="small" variant="outlined" />
                            )}
                          </Stack>
                          {!channelAllowExpanded && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {channelAllowActive
                                ? (summarizeUniiSelection(channelGroupIds) || 'Custom channel lists set')
                                : `${defaultOptimizeChannelGroupSummary()} (default backhaul)`}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      <Collapse in={channelAllowExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 1.5, pb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            Limit channel assignments to selected UNII groups and/or explicit per-band lists. DSATUR only picks from the union.
                          </Typography>
                          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                            <InputLabel>UNII groups</InputLabel>
                            <Select
                              multiple
                              label="UNII groups"
                              value={channelGroupIds}
                              onChange={(e) => {
                                const raw = e.target.value;
                                setChannelGroupIds(typeof raw === 'string' ? raw.split(',').map(Number) : raw);
                              }}
                              renderValue={(selected) => (
                                selected.length === 0
                                  ? `${defaultOptimizeChannelGroupSummary()} (default)`
                                  : selected.map((id) => UNII_CHANNEL_GROUPS.find((g) => g.id === id)?.label || id).join(', ')
                              )}
                            >
                              {UNII_CHANNEL_GROUPS.map((g) => (
                                <MenuItem key={g.id} value={g.id}>
                                  <Checkbox size="small" checked={channelGroupIds.includes(g.id)} />
                                  <ListItemText primary={formatUniiOption(g)} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="5 GHz channels"
                                size="small"
                                fullWidth
                                value={channelAllowList5GHz}
                                onChange={(e) => setChannelAllowList5GHz(e.target.value)}
                                placeholder="36, 40, 44, 48"
                                helperText="Comma-separated; unioned with UNII selection"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="6 GHz channels"
                                size="small"
                                fullWidth
                                value={channelAllowList6GHz}
                                onChange={(e) => setChannelAllowList6GHz(e.target.value)}
                                placeholder="5, 13, 21"
                                helperText="Comma-separated; unioned with UNII selection"
                              />
                            </Grid>
                          </Grid>
                          {channelAllowActive && (
                            <Button
                              size="small"
                              sx={{ mt: 1 }}
                              onClick={() => {
                                setChannelGroupIds([]);
                                setChannelAllowList5GHz('');
                                setChannelAllowList6GHz('');
                              }}
                            >
                              Clear restriction
                            </Button>
                          )}
                        </Box>
                      </Collapse>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip
                      arrow
                      placement="top"
                      title="When enabled, the channel planner still evaluates the full mesh but only CAP radios receive channel actions. Node AP radios stay on their current channel (frozen as constraints). Use this for CAP-only retunes; leave off when child downlink radios should move (default)."
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={capOnlyActions}
                            onChange={(e) => setCapOnlyActions(e.target.checked)}
                          />
                        }
                        label={<Typography variant="caption">CAP-only channel actions</Typography>}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip
                      arrow
                      placement="top"
                      title="Default on: AP devices with no mesh children and not on the reparent whitelist are dropped from heard/conflict planning (and a second pass may retry without them if pairs stay unsatisfied). Turn off to keep every AP in the planner graph (idle_ap_retry_disabled)."
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={excludeIdleAPs}
                            onChange={(e) => setExcludeIdleAPs(e.target.checked)}
                          />
                        }
                        label={<Typography variant="caption">Exclude unused APs from channel graph</Typography>}
                      />
                    </Tooltip>
                  </Grid>

                  {/* Per-role channel planning (Jun 2026) */}
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1, mb: 0.25 }}>
                      Per-role channel planning
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      CAP backhaul radios plan wider (40 MHz) with a tighter same-chassis floor; node/hop
                      treemgmt radios plan at 20 MHz. Leaving DSATUR allowed channels empty defaults to
                      U-NII-5 + U-NII-7 (6 GHz groups 3 &amp; 5).
                    </Typography>
                  </Grid>
                  <Grid item xs={3} md={2}>
                    <Tooltip title="DSATUR planning bandwidth for CAP backhaul radios (MHz). Drives candidate filtering and the emitted htmode. Default 40.">
                      <FormControl size="small" fullWidth>
                        <InputLabel>CAP BW</InputLabel>
                        <Select
                          label="CAP BW"
                          value={capChannelBandwidth}
                          onChange={(e) => setCapChannelBandwidth(Number(e.target.value))}
                        >
                          <MenuItem value={20}>20 MHz</MenuItem>
                          <MenuItem value={40}>40 MHz</MenuItem>
                          <MenuItem value={80}>80 MHz</MenuItem>
                        </Select>
                      </FormControl>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={3} md={2}>
                    <Tooltip title={`Per-role same-chassis center-frequency floor for CAP radios (MHz). Default ${DEFAULT_CAP_SAME_CHASSIS_MIN_SEP_MHZ}.`}>
                      <NumericParamField
                        label="CAP chassis gap"
                        size="small"
                        fullWidth
                        value={capSameChassisMinSep}
                        onChange={setCapSameChassisMinSep}
                        fallback={DEFAULT_CAP_SAME_CHASSIS_MIN_SEP_MHZ}
                        min={0}
                        inputProps={{ step: 20 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={3} md={2}>
                    <Tooltip title="DSATUR planning bandwidth for node/hop treemgmt radios (MHz). Default 20.">
                      <FormControl size="small" fullWidth>
                        <InputLabel>Hop BW</InputLabel>
                        <Select
                          label="Hop BW"
                          value={hopChannelBandwidth}
                          onChange={(e) => setHopChannelBandwidth(Number(e.target.value))}
                        >
                          <MenuItem value={20}>20 MHz</MenuItem>
                          <MenuItem value={40}>40 MHz</MenuItem>
                          <MenuItem value={80}>80 MHz</MenuItem>
                        </Select>
                      </FormControl>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={3} md={2}>
                    <Tooltip title={`Per-role same-chassis center-frequency floor for node/hop radios (MHz). Default ${DEFAULT_HOP_SAME_CHASSIS_MIN_SEP_MHZ}.`}>
                      <NumericParamField
                        label="Hop chassis gap"
                        size="small"
                        fullWidth
                        value={hopSameChassisMinSep}
                        onChange={setHopSameChassisMinSep}
                        fallback={DEFAULT_HOP_SAME_CHASSIS_MIN_SEP_MHZ}
                        min={0}
                        inputProps={{ step: 20 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip arrow placement="top" title="Default on: emit channel_groups for every in-scope backhaul radio — even ones whose channel doesn't change — so the device's ACS is locked to the DSATUR-chosen channel instead of the whole band.">
                      <FormControlLabel
                        control={<Switch size="small" checked={fixChannelGroups} onChange={(e) => setFixChannelGroups(e.target.checked)} />}
                        label={<Typography variant="caption">Lock channel_groups to coloring</Typography>}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip arrow placement="top" title="Default on: respect treemgmt segment_id_ap / segment_id_dev when anchoring CAP backhaul. Off treats the mesh as a single open segment.">
                      <FormControlLabel
                        control={<Switch size="small" checked={segmentAware} onChange={(e) => setSegmentAware(e.target.checked)} />}
                        label={<Typography variant="caption">Segment-aware</Typography>}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip arrow placement="top" title="Default on: drop radios running a Public WiFi AP from the backhaul DSATUR graph (managed by the Public WiFi RRM planner).">
                      <FormControlLabel
                        control={<Switch size="small" checked={excludePublicWifiRadios} onChange={(e) => setExcludePublicWifiRadios(e.target.checked)} />}
                        label={<Typography variant="caption">Exclude Public WiFi radios</Typography>}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip arrow placement="top" title="Default on: drop radios with treemgmt max_children = 0 (mesh disabled there) from the backhaul DSATUR graph.">
                      <FormControlLabel
                        control={<Switch size="small" checked={excludeMaxChildrenZeroRadios} onChange={(e) => setExcludeMaxChildrenZeroRadios(e.target.checked)} />}
                        label={<Typography variant="caption">Exclude max_children=0 radios</Typography>}
                      />
                    </Tooltip>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Power */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { mt: 0 },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  TX Power
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} md={2}>
                    <Tooltip title="Minimum dB change a power action must clear before it surfaces in the chained plan. 3 dB is the backend default; the analyser still considers 2 dB internally.">
                      <NumericParamField
                        size="small"
                        fullWidth
                        label="Power floor (dB)"
                        value={powerImprovementFloorDb}
                        onChange={setPowerImprovementFloorDb}
                        fallback={DEFAULT_POWER_IMPROVEMENT_FLOOR_DB}
                        min={0}
                        max={12}
                        inputProps={{ step: 1 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Tooltip title="Uplink severity weight. 1.0 = symmetric (default, no bias). 1.5 = legacy edge-TX-dominant bias that surfaces uplink recommendations first.">
                      <NumericParamField
                        size="small"
                        fullWidth
                        label="Uplink weight"
                        value={powerUplinkSeverityWeight}
                        onChange={setPowerUplinkSeverityWeight}
                        fallback={DEFAULT_POWER_UPLINK_SEVERITY_WEIGHT}
                        min={0.1}
                        max={3}
                        inputProps={{ step: 0.1 }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Tooltip title="EIRP regulatory cap table for the power stage. 'default' applies no extra restriction beyond the 18 dBm hardware cap. 'eu' / 'il' (23 dBm EIRP) and 'us' (36 dBm EIRP) clamp recommended TX via (EIRP_cap − antenna_gain).">
                      <FormControl size="small" fullWidth>
                        <InputLabel>Power reg. domain</InputLabel>
                        <Select
                          label="Power reg. domain"
                          value={powerRegulatoryDomain}
                          onChange={(e) => setPowerRegulatoryDomain(e.target.value)}
                        >
                          <MenuItem value="default">Default (hardware cap only)</MenuItem>
                          <MenuItem value="us">US — 36 dBm EIRP</MenuItem>
                          <MenuItem value="eu">EU — 23 dBm EIRP</MenuItem>
                          <MenuItem value="il">IL — 23 dBm EIRP</MenuItem>
                        </Select>
                      </FormControl>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Tooltip title="CAP antenna gain in dBi. Default 11 (directional sector). Subtracted from the EIRP cap to compute the effective per-radio TX limit. Only takes effect when Power reg. domain is non-default.">
                      <NumericParamField
                        size="small"
                        fullWidth
                        label="CAP gain (dBi)"
                        value={powerCapAntennaGainDbi}
                        onChange={setPowerCapAntennaGainDbi}
                        fallback={DEFAULT_POWER_CAP_ANTENNA_GAIN_DBI}
                        min={0}
                        max={30}
                        inputProps={{ step: 0.5 }}
                        disabled={!powerRegulatoryDomain || powerRegulatoryDomain === 'default'}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Tooltip title="Node antenna gain in dBi. Default 0 (omni). Subtracted from the EIRP cap to compute the effective per-radio TX limit. Only takes effect when Power reg. domain is non-default.">
                      <NumericParamField
                        size="small"
                        fullWidth
                        label="Node gain (dBi)"
                        value={powerNodeAntennaGainDbi}
                        onChange={setPowerNodeAntennaGainDbi}
                        fallback={DEFAULT_POWER_NODE_ANTENNA_GAIN_DBI}
                        min={0}
                        max={30}
                        inputProps={{ step: 0.5 }}
                        disabled={!powerRegulatoryDomain || powerRegulatoryDomain === 'default'}
                      />
                    </Tooltip>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Paper>
      </Collapse>

      {!result && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle sx={{ mb: 0.5 }}>Ready to plan</AlertTitle>
          Click <strong>Run</strong> to compute a chained plan across Health → Topology →
          TX-Power → Channels → Backup. Nothing is changed on the network until you
          click <em>Apply</em> on a step or the entire plan.
        </Alert>
      )}

      {result && (
        <>
          {/* Composite gauge + headline scores */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Stack alignItems="center" spacing={1} sx={{ height: '100%', justifyContent: 'center' }}>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    {/* Track ring (subtle) */}
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={132}
                      thickness={4}
                      sx={{ color: 'action.hover' }}
                    />
                    {/* Value ring */}
                    <CircularProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, compositeScore || 0))}
                      size={132}
                      thickness={4}
                      color={compositeTone === 'inherit' ? 'inherit' : compositeTone}
                      sx={{
                        position: 'absolute',
                        left: 0,
                        ...(compositeTone === 'inherit' ? { color: 'text.secondary' } : {}),
                      }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 600, lineHeight: 1 }}>
                        {isFiniteNumber(compositeScore) ? `${compositeScore.toFixed(0)}%` : '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        weighted impact
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Composite improvement
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 240 }}>
                    {composite?.summary_text || 'Composite of topology, channel and power improvements.'}
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs={12} md={8}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                  End-to-end improvements
                </Typography>
                <Stack spacing={1}>
                  {/* Phase 2: prefer the path-throughput headline
                      (higher-is-better kbps) over the legacy
                      tree_cost summed-cost score. The latter is
                      still surfaced via metric_help on the path
                      score's tooltip for diagnostics. */}
                  {(result.topology_path_score || result.topology_score) && (
                    <ScoreCard
                      score={result.topology_path_score || result.topology_score}
                      title={result.topology_path_score ? 'Topology (path throughput)' : 'Topology (cost)'}
                      dense
                    />
                  )}
                  {result.channel_score && <ScoreCard score={result.channel_score} title="Channels (conflicts)" dense />}
                  {result.power_score && <ScoreCard score={result.power_score} title="TX Power (mW)" dense />}
                  {(result.health_score || result.backup_score) && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 0.5 }}>
                      {result.health_score && (
                        <Box sx={{ flex: 1 }}>
                          <ScoreCard score={result.health_score} title="Health" dense compact />
                        </Box>
                      )}
                      {result.backup_score && (
                        <Box sx={{ flex: 1 }}>
                          <ScoreCard score={result.backup_score} title="Backup parents" dense compact />
                        </Box>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Grid>
            </Grid>

            {/* Convergence + per-metric % chips — all neutral / outlined
                so we don't pull focus away from the gauge. */}
            <Divider sx={{ my: 2 }} />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} useFlexGap flexWrap="wrap">
              <Tooltip title={`Stopped: ${result.convergence?.stopped_reason || 'unknown'}`}>
                <Chip
                  size="small"
                  label={`${result.convergence?.iterations || 0} iteration(s)`}
                  variant="outlined"
                />
              </Tooltip>
              <Chip size="small" variant="outlined" label={`${result.summary?.topology_actions || 0} topology`} />
              <Chip size="small" variant="outlined" label={`${result.summary?.power_actions || 0} power`} />
              <Chip size="small" variant="outlined" label={`${result.summary?.channel_actions || 0} channel`} />
              <Chip size="small" variant="outlined" label={`${result.summary?.backup_advisories || 0} backup`} />
              {result.convergence?.p10_guard_pruned > 0 && (
                <Tooltip
                  arrow
                  placement="top"
                  title={result.convergence?.p10_guard_note || 'Topology actions pruned to prevent p10 regression'}
                >
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${result.convergence.p10_guard_pruned} pruned (p10 guard)`}
                  />
                </Tooltip>
              )}
              {result.cohort_scope?.isolated && (
                <Tooltip
                  title={`Isolated cohort: ${result.cohort_scope.analysis_serials?.length || 0} device(s) in analysis${
                    result.cohort_scope.excluded_site_devices
                      ? `; ${result.cohort_scope.excluded_site_devices} site device(s) excluded`
                      : ''
                  }`}
                >
                  <Chip
                    size="small"
                    color="secondary"
                    variant="outlined"
                    label={`Isolated cohort (${result.cohort_scope.analysis_serials?.length || 0})`}
                  />
                </Tooltip>
              )}
              {result.custom_tree_scope && (
                <Tooltip title={`Scoped to custom tree ${result.custom_tree_scope.name || result.custom_tree_scope.id} (segment ${result.custom_tree_scope.segment_id})`}>
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label={`Tree: ${result.custom_tree_scope.name || result.custom_tree_scope.id}`}
                  />
                </Tooltip>
              )}
              {result.iperf_anchored && result.iperf_run && (
                <Tooltip
                  arrow
                  placement="top"
                  title={result.iperf_run.merged && Array.isArray(result.iperf_run.segments)
                    ? `Anchored on ${result.iperf_run.segments.length} segment run(s): ${result.iperf_run.segments.map((seg) => seg.name || seg.id?.slice(0, 8)).filter(Boolean).join(', ')}`
                    : `Anchored on iperf run ${result.iperf_run.name || result.iperf_run.id || ''}${result.iperf_run.completed_at ? ` (${new Date(result.iperf_run.completed_at).toLocaleString()})` : ''}`}
                >
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={result.iperf_run.merged
                      ? `iperf-anchored (${result.iperf_run.segments?.length || 0} segments, ${Math.round((result.iperf_run.match_score || 0) * 100)}% avg)`
                      : `iperf-anchored (${Math.round((result.iperf_run.match_score || 0) * 100)}% edges)`}
                  />
                </Tooltip>
              )}
              {result.iperf_requested && !result.iperf_anchored && (
                <Tooltip
                  arrow
                  placement="top"
                  title={optimizeScopeIsSiteWide
                    ? 'Iperf was requested but no site-wide run has any topology overlap with the current tree. The plan was computed using theoretical estimates only.'
                    : 'Iperf was requested but no run overlaps the selected optimize scope. The plan was computed using theoretical estimates only.'}
                >
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="no matching iperf"
                  />
                </Tooltip>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {composite && (
                <Stack direction="row" spacing={0.75}>
                  <Tooltip title="Topology improvement vs initial state">
                    <Chip size="small" variant="outlined" label={`Topo ${formatPct(composite.topology_improvement_pct) || '—'}`} />
                  </Tooltip>
                  <Tooltip title="Channel improvement vs initial state">
                    <Chip size="small" variant="outlined" label={`Chan ${formatPct(composite.channel_improvement_pct) || '—'}`} />
                  </Tooltip>
                  <Tooltip title="Power improvement vs initial state">
                    <Chip size="small" variant="outlined" label={`Pow ${formatPct(composite.power_improvement_pct) || '—'}`} />
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          </Paper>

          {Array.isArray(result.segment_warnings) && result.segment_warnings.length > 0 && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              <AlertTitle>Segment advisories ({result.segment_warnings.length})</AlertTitle>
              <Stack spacing={1}>
                {result.segment_warnings.map((warn) => (
                  <Typography key={`${warn.warning_id}-${warn.serial}`} variant="body2">
                    <strong>{warn.serial}</strong>
                    {' · '}
                    {warn.warning_id}
                    {' — '}
                    {warn.reason}
                    {(warn.segment_id_dev?.length || warn.segment_id_ap?.length) ? (
                      <>
                        {' '}
                        (dev: [{ (warn.segment_id_dev || []).join(', ') || '—' }];
                        ap: [{ (warn.segment_id_ap || []).join(', ') || '—' }])
                      </>
                    ) : null}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <AlertTitle>Telemetry usage in this optimize run</AlertTitle>
            <Stack spacing={1}>
              {uplinkUsage.healthTelemetryIssues > 0 && (
                <Typography variant="body2">
                  <strong>Health (rf_telemetry):</strong>{' '}
                  <strong>{uplinkUsage.healthTelemetryIssues}</strong> of{' '}
                  <strong>{uplinkUsage.healthTotalIssues}</strong> health issue(s) came from
                  rf_telemetry (airtime saturation, external interference, RF errors, or link
                  retry bursts). These issue types only appear when devices report rf_telemetry
                  counters.
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Topology:</strong> uses the child&apos;s STA-mode uplink profile as the
                primary current-edge signal when available. Dominant MCS + NSS + BW are converted
                to an estimated PHY kbps and folded into link cost / quality class; parent-side
                measurement and RSSI are fallbacks. When rf_telemetry is available, phy_rate_mbps
                is preferred and recent per-window loss/retry deltas replace cumulative ratios.
                This response shows child-uplink telemetry on{' '}
                <strong>{uplinkUsage.topologyMeasured}</strong> of{' '}
                <strong>{uplinkUsage.topologyActions}</strong> topology action(s), and proposed-edge
                PHY estimates on <strong>{uplinkUsage.topologyEstimated}</strong> action(s).
              </Typography>
              {uplinkUsage.example && (
                <Typography variant="caption" color="text.secondary">
                  Example: {uplinkUsage.example.serial} current uplink{' '}
                  {isFiniteNumber(uplinkUsage.example.current_uplink_mcs) ? `MCS ${uplinkUsage.example.current_uplink_mcs}` : 'MCS —'}
                  {isFiniteNumber(uplinkUsage.example.current_uplink_nss) ? ` / NSS ${uplinkUsage.example.current_uplink_nss}` : ''}
                  {isFiniteNumber(uplinkUsage.example.current_uplink_bw_mhz) ? ` / ${uplinkUsage.example.current_uplink_bw_mhz} MHz` : ''}
                  {formatKbpsShort(uplinkUsage.example.current_uplink_est_kbps)
                    ? ` → ${formatKbpsShort(uplinkUsage.example.current_uplink_est_kbps)} estimated PHY`
                    : ''}
                  {uplinkUsage.example.current_uplink_source_radio
                    ? ` from ${uplinkUsage.example.current_uplink_source_radio}`
                    : ''}
                  .
                </Typography>
              )}
              {uplinkUsage.powerSuppressed > 0 && (
                <Typography variant="body2">
                  <strong>TX Power (rf_telemetry gates):</strong>{' '}
                  <strong>{uplinkUsage.powerSuppressed}</strong> of{' '}
                  <strong>{uplinkUsage.powerTotalIssues}</strong> power issue(s) had their action
                  suppressed by an rf_telemetry safety gate (ack_failure_rate ≥ 5% or
                  tx_airtime_pct ≥ 40%). These are still shown as issues with a SuppressedReason
                  in the Power step below.
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Channels:</strong> uses mean child uplink MCS to weight conflicts. Low-MCS
                APs get higher severity because they consume more airtime per byte. When
                rf_telemetry is available, conflict severity uses foreign airtime (channel_busy
                − own TX/RX) instead of the legacy lumped busy fraction, and candidate channels
                are demoted by FCS-error penalty. This response includes mean child MCS on{' '}
                <strong>{uplinkUsage.channelWeighted}</strong> of{' '}
                <strong>{uplinkUsage.channelIssues}</strong> channel issue(s).
              </Typography>
              {uplinkUsage.idleAPPlanStatsText && (
                <Typography variant="body2">
                  <strong>Unused AP handling:</strong> {uplinkUsage.idleAPPlanStatsText}
                </Typography>
              )}
              {uplinkUsage.channelUnsatisfied > 0 && (
                <Typography variant="body2">
                  <strong>Unsatisfied heard-pairs:</strong>{' '}
                  <strong>{uplinkUsage.channelUnsatisfied}</strong> constraint(s) could not be resolved
                  by channel assignment alone (may need topology or bandwidth changes).
                </Typography>
              )}
              {uplinkUsage.channelNoActionExplain && (
                <Alert severity={uplinkUsage.channelNoActionExplain.severity} variant="outlined" sx={{ mt: 1 }}>
                  <AlertTitle sx={{ fontWeight: 600 }}>
                    Channels: {uplinkUsage.channelNoActionExplain.title}
                  </AlertTitle>
                  <Typography variant="body2">
                    {uplinkUsage.channelNoActionExplain.summary}
                  </Typography>
                </Alert>
              )}
              {uplinkUsage.channelBWDowngrades > 0 && (
                <Typography variant="body2">
                  <strong>Bandwidth downgrades:</strong>{' '}
                  <strong>{uplinkUsage.channelBWDowngrades}</strong> channel action(s) include a
                  bandwidth reduction (e.g. HT80 → HT20) because no same-bandwidth channel could
                  resolve the conflict. These actions trade throughput for interference isolation.
                </Typography>
              )}
              {uplinkUsage.channelPlanStats?.idle_ap_retry_applied && (
                <Typography variant="body2">
                  <strong>Idle AP second pass:</strong> excluded{' '}
                  <strong>{(uplinkUsage.channelPlanStats.idle_ap_devices_excluded || []).length}</strong>{' '}
                  unused AP device(s) after{' '}
                  <strong>{uplinkUsage.channelPlanStats.unsatisfied_pairs_before}</strong> unsatisfied
                  heard-pair(s);{' '}
                  <strong>{uplinkUsage.channelPlanStats.unsatisfied_pairs_after}</strong> remain.
                </Typography>
              )}
              {uplinkUsage.channelTriageActive && (
                <Typography variant="body2">
                  <strong>Channel pair triage:</strong> GPS distance is now a coarse pre-filter; the
                  planner additionally checks each device&apos;s neighbour scan report and drops
                  pairs that are GPS-close but RF-isolated (walls, etc.). Of{' '}
                  <strong>{uplinkUsage.channelIssues}</strong> surviving issue(s),{' '}
                  <strong>{uplinkUsage.channelHeard}</strong> were confirmed by scan
                  (&quot;heard&quot; with reported RSSI), and{' '}
                  <strong>{uplinkUsage.channelNoScan}</strong> kept on GPS-only because at least one
                  side has no scan data yet (will tighten as more devices report scans).
                </Typography>
              )}
            </Stack>
          </Alert>

          {/* Apply-all bar — buttons aligned right, single primary
              accent so the eye lands on the right action. */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {allActions.length} action{allActions.length === 1 ? '' : 's'} in this plan
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Order matches the meta-pipeline. Apply step-by-step for safety, or the
                  entire plan in one go.
                </Typography>
                <FormControlLabel
                  sx={{ mt: 0.5, mb: 0, alignItems: 'flex-start' }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={planPersistTree}
                      onChange={(e) => setPlanPersistTree(e.target.checked)}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography variant="body2" component="span">
                        Pin tree before apply
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Lock devices that are not in this plan to their current parent (whitelist + insist)
                        before running plan actions. Recommended for topology changes.
                      </Typography>
                    </Box>
                  )}
                />
                <FormControlLabel
                  sx={{ mt: 0.5, mb: 0, alignItems: 'flex-start' }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={tolerantReparent}
                      onChange={(e) => setTolerantReparent(e.target.checked)}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography variant="body2" component="span">
                        Tolerant reparent (background scan)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Enable background_sta_scan during reparent so devices keep probing for the new
                        parent if it&apos;s briefly unreachable. Extends the settle window to 20 minutes
                        and disables scanning automatically when the action terminates.
                      </Typography>
                    </Box>
                  )}
                />
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                {applyAllBusy && applyProgress.total > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${applyProgress.done} / ${applyProgress.total}`}
                  />
                )}
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={applyAllBusy ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                  onClick={applyEntirePlan}
                  disabled={applyAllBusy || allActions.length === 0}
                >
                  Apply entire plan
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {result.input_debug && (
            <InputDebugPanel inputDebug={result.input_debug} />
          )}

          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
            Step-by-step plan
          </Typography>

          {result.steps?.length === 0 && (
            <Alert severity="success">No steps produced — tree already optimal.</Alert>
          )}

          {result.steps?.map((step, idx) => {
            const label = STEP_LABEL[step.step] || step.step;
            const actionsCount = stepActionsCount(step);
            const issuesCount = stepIssuesCount(step);
            const stepEntries = flattenAllActions([step]);
            return (
              <Accordion
                key={`${step.step}-${step.iteration}-${idx}`}
                // Expand by default when the step is the first one,
                // when it produced actions, when it surfaced issues
                // worth investigating, or when it carries advisory
                // backup entries — i.e. anything actionable or
                // diagnostic.
                defaultExpanded={
                  idx === 0
                  || actionsCount > 0
                  || issuesCount > 0
                  || (step.backup_entries?.length || 0) > 0
                }
                sx={{
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  mb: 1,
                  borderRadius: 1,
                  '&.Mui-expanded': { mb: 1 },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                    <Chip
                      label={`#${idx + 1}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600, minWidth: 36 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {label}
                    </Typography>
                    {step.iteration > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        iter {step.iteration}
                      </Typography>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Chip
                      size="small"
                      label={`${actionsCount} actions`}
                      variant="outlined"
                      color={actionsCount > 0 ? 'primary' : 'default'}
                    />
                    {issuesCount > 0 && (
                      <Chip size="small" variant="outlined" label={`${issuesCount} issues`} />
                    )}
                    {step.step === 'topology' && [
                      ['skipped_due_to_pin', 'pinned', 'Skipped because a previous step already touched this node'],
                      ['skipped_due_to_floor', 'floor', 'Below improvement floor (misassociation uses improvement_floor)'],
                      ['skipped_due_to_local_opportunistic', 'local', 'Good link with insufficient tree-wide p10 gain'],
                      ['skipped_due_to_holistic_threshold', 'holistic', 'Tree-holistic move below Δp10 threshold'],
                      ['skipped_due_to_parent_power_recovery', 'TX defer', 'Deferred — parent TX raise may recover the link'],
                      ['skipped_due_to_parent_channel_recovery', 'CH defer', 'Deferred — parent channel move may recover the link'],
                    ].map(([key, label, title]) => (
                      (step[key] || 0) > 0 && (
                        <Tooltip key={key} title={title}>
                          <Chip size="small" variant="outlined" label={`${step[key]} ${label}`} />
                        </Tooltip>
                      )
                    ))}
                    {step.step === 'channel' && issuesCount > 0 && actionsCount === 0 && step.channel_plan_stats?.no_action_reason && (
                      <Tooltip title="Why no channel actions were emitted despite visible conflicts">
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          label={step.channel_plan_stats.no_action_reason.replace(/_/g, ' ')}
                        />
                      </Tooltip>
                    )}
                    {step.step === 'channel' && [
                      ['skipped_due_to_pin', 'pinned', 'Planner action skipped — device/radio pinned by an earlier step'],
                      ['skipped_due_to_focus', 'focus', 'Outside optimize focus scope'],
                      ['skipped_due_to_floor', 'floor', 'Below per-action channel improvement floor'],
                    ].map(([key, label, title]) => (
                      (step[key] || 0) > 0 && (
                        <Tooltip key={key} title={title}>
                          <Chip size="small" variant="outlined" label={`${step[key]} ${label}`} />
                        </Tooltip>
                      )
                    ))}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Per-step framing — outlined info Alert so it
                      visually separates from the existing filled
                      `step.note` Alert below it. Reads as
                      "documentation about this step", not "warning
                      from this run". */}
                  {STEP_DESCRIPTION[step.step] && (
                    <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
                      {STEP_DESCRIPTION[step.step]}
                    </Alert>
                  )}
                  {step.score && (
                    <Box sx={{ mb: 1.5 }}>
                      <ScoreCard score={step.score} compact />
                    </Box>
                  )}
                  <ChannelNoActionExplainAlert step={step} />
                  {step.note && (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      {step.note}
                    </Alert>
                  )}
                  {stepEntries.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      {issuesCount > 0
                        ? 'No actions at this step — see the issues list below for what the analyser found but couldn\'t safely act on.'
                        : ((step.backup_entries?.length || 0) > 0
                          ? 'No actions at this step (backup is advisory) — see the per-node failover posture below.'
                          : 'No actions at this step.')}
                    </Typography>
                  ) : (
                    <Box>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                        <Box sx={{ flexGrow: 1 }} />
                        <Tooltip title="Apply only the actions of this step">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PlayArrowIcon />}
                              onClick={() => applyOneStep(stepEntries, `${label} step`)}
                              disabled={applyAllBusy}
                            >
                              Apply step ({stepEntries.length})
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, width: 90 }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                              <TableCell sx={{ fontWeight: 600, width: 220 }}>Why</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stepEntries.map((entry, k) => (
                              <TableRow key={`${entry.action.action_id || k}`} hover>
                                <TableCell>
                                  <Chip size="small" variant="outlined" label={entry.kind} />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                    {formatActionLine(entry)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption" color="text.secondary">
                                    {entry.action.reason || '—'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* Diagnostic surfaces: the meta endpoint already
                      carries per-step issues + backup entries; render
                      them so the operator can understand *why* a step
                      produced 0 actions instead of just seeing an
                      empty actions table. */}
                  <StepIssuesTable issues={step.health_issues}   kindLabel="Health"   />
                  <StepIssuesTable issues={step.topology_issues} kindLabel="Topology" />
                  <StepIssuesTable issues={step.power_issues}    kindLabel="Power"    />
                  <StepIssuesTable issues={step.channel_issues}  kindLabel="Channel"  />
                  <BackupEntriesTable entries={step.backup_entries} />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      )}
    </Box>
  );
};

export default OptimizeMetaPanel;
